/**
 * Application entry point.
 *
 *   input ──► FlightControls ──┐
 *                             ├─► FlightDynamicsModel.updateFixed (120 Hz)
 *   wind, atmosphere ──────────┘              │
 *                                             ├─► WorldSystem.updateFixed  (origin, ground)
 *                                             ▼
 *                        FlightScene.updateFrame (interpolated by alpha)
 *                                             │
 *                                             └─► WorldSystem.updateFrame  (terrain streaming)
 *
 *   quality governor ◄── Profiler ◄── loop instrumentation
 *
 * Nothing here runs per physics tick beyond forwarding the controls and keeping the floating
 * origin under the aircraft; this file is start-up, event wiring, and the handful of key
 * bindings that belong to the shell rather than the aircraft.
 */

import './ui/overlay.css';

import { Vector3 } from 'three';
import { PRODUCT_NAME } from './brand.js';
import { GameLoop } from './core/loop.js';
import { AllocationProbe } from './core/profiler.js';
import { QualityGovernor } from './core/governor.js';
import { QualityManager, type QualityTier, TIERS } from './core/quality.js';
import { rng } from './core/rng.js';
import { DEG2RAD } from './core/math.js';
import { Keyboard } from './input/keyboard.js';
import { PointerControls } from './input/pointer.js';
import { FlightControls } from './input/flightControls.js';
import type { CoordinationFeedback } from './input/coordination.js';
import { Atmosphere, WindModel } from './sim/atmosphere.js';
import { FlightDynamicsModel } from './sim/fdm.js';
import { aeroToRenderVector, eulerToQuaternion } from './sim/frames.js';
import { engineIdleSpeed, engineReference, loadAircraft } from './aircraft/config.js';
import { AIRCRAFT, aircraftById, requestedAircraftId } from './aircraft/registry.js';
import { WeaponSystem, makeWeaponInput } from './aircraft/weapons/weaponSystem.js';
import { HitQueue, NO_TARGETS, type TargetList } from './aircraft/weapons/engagement.js';
import {
  BALLOON_COURSE_HEADING_DEG,
  CANYON_COURSE,
  GunneryMode,
  type CourseId,
} from './game/modes/gunnery.js';
import { CanyonRun, requestedPerfScene } from './game/canyonRun.js';
import { formatTime } from './game/scoring.js';
import { CombatEffects } from './render/effects/combatEffects.js';
import { CombatView } from './render/combatView.js';
import { AfterburnerPlume } from './render/effects/afterburner.js';
import { AirportDatabase, longestRunway, type Runway } from './world/airports/db.js';
import { AIRPORT_DB_URL } from './world/airports/format.js';
import { finalApproachSpawn, thresholdSpawn } from './world/airports/runwayMesh.js';
import { FlightScene } from './render/flightScene.js';
import {
  ORIGIN_REBASE_DISTANCE_M,
  originRebaseDistanceM,
  setOriginRebaseDistanceM,
} from './world/geo.js';
import { cockpitProfileFor } from './render/cockpit.js';
import { loadAircraftModel } from './render/aircraftModel.js';
import { loadCockpitModel } from './render/cockpitModel.js';
import { TIME_PRESETS, type TimePresetName } from './render/environment.js';
import { WEATHER_PRESETS, windLayersFor, type WeatherPresetName } from './world/weather.js';
import { applyQualityToRenderer, createRenderer, resizeRenderer } from './render/renderer.js';
import { PostChain } from './render/postProcessing.js';
import { ErrorOverlay } from './ui/errorOverlay.js';
import { PerfOverlay } from './ui/perfOverlay.js';
import { FlightHud } from './ui/flightHud.js';
import { MenuShell } from './ui/menu.js';
import { FighterHud, makeHudInputs, makeWeaponsHudState, readHud } from './aircraft/fighterHud.js';
import { makeInstrumentInputs, readInstruments } from './aircraft/instruments.js';
import { LocalStorageService } from './core/storage.js';
import { SettingsStore, loadLastFlight, saveLastFlight, type CameraMode, type Settings } from './game/settings.js';
import { BindingStore, keyLabel } from './input/bindings.js';
import { FirstFlightDirector } from './ui/firstFlight.js';
import { LandingScorer, projectToRunway, type RunwayEnds } from './game/modes/landing.js';
import { LandingRecordKeeper } from './game/landingRecords.js';
import { PLACEMENT } from './world/airports/format.js';
import { knotsToMps } from './core/units.js';
import { HelpFooter } from './ui/helpFooter.js';
import { SpawnPicker, type SpawnChoice } from './ui/spawnPicker.js';
import { EnvironmentPanel } from './ui/environmentPanel.js';
import {
  PerfHarness,
  requestedPerfSeconds,
  requestedPerfWarmupSeconds,
  type StreamingMarks,
} from './game/perfHarness.js';
import {
  ScreenshotRun,
  requestedCardSize,
  requestedProbes,
  requestedSettleSeconds,
  requestedShotName,
} from './game/screenshotRun.js';
import { ShareController } from './game/screenshot.js';
import { ShareButton } from './ui/shareButton.js';
import { NoticeBanner } from './ui/noticeBanner.js';
import { ConnectionMonitor, connectionMessage } from './core/connectivity.js';
import { AppUpdates } from './core/serviceWorker.js';
import {
  isTouchPrimary,
  readPlatformSignals,
  shouldPromptForLandscape,
  startTierFor,
} from './core/platform.js';
import { TouchControls } from './input/touch.js';
import { LandscapePrompt } from './ui/landscapePrompt.js';
import { AudioSystem } from './audio/audioSystem.js';
import { soundConfigFor } from './audio/soundSet.js';

/** Where a session starts when nothing else says otherwise. */
const DEFAULT_ICAO = 'KSFO';

/**
 * What a crash report should say about the machine it happened on.
 *
 * Filled in as bootstrap learns each answer, and read only when something has already gone wrong.
 * Mutable module state rather than a constructor argument because the overlay has to exist *before*
 * the renderer does — it is what catches a renderer that fails to initialise at all, which is the
 * single most likely thing for it to be reporting.
 */
const diagnostics = { backend: 'not yet initialised', tier: 'not yet chosen', device: 'unknown' };

const errorOverlay = new ErrorOverlay({
  context: () => [
    `renderer: ${diagnostics.backend}`,
    `tier: ${diagnostics.tier}`,
    `input: ${diagnostics.device}`,
  ],
});
errorOverlay.install();

void bootstrap().catch((error: unknown) => {
  console.error(error);
  errorOverlay.show(error, `${PRODUCT_NAME} failed to start`);
});

async function bootstrap(): Promise<void> {
  const canvasElement = document.getElementById('skysim-canvas');
  if (!(canvasElement instanceof HTMLCanvasElement)) {
    throw new Error('#skysim-canvas is missing from the document');
  }
  const canvas: HTMLCanvasElement = canvasElement;
  const bootSplash = document.getElementById('skysim-boot');
  const bootStatus = document.getElementById('skysim-boot-status');
  const setStatus = (text: string): void => {
    if (bootStatus !== null) bootStatus.textContent = text;
  };

  // --- persistence --------------------------------------------------------------------
  /*
   * One storage service, made before anything else, because everything below reads from it.
   * It never throws — see `core/storage.ts` — so a browser that refuses storage gets the shipped
   * defaults and a session that simply is not remembered, rather than a page that fails to start.
   */
  const storage = new LocalStorageService();
  const settingsStore = new SettingsStore(storage);
  settingsStore.load();
  const bindings = new BindingStore(storage);
  bindings.load();

  // --- what kind of machine is this? ----------------------------------------------------
  /*
   * Read before anything else that depends on it, which is the quality tier and the input layer.
   * `?touch=1` forces the touch tier onto a desktop and `?touch=0` takes it off a tablet — see
   * `core/platform.ts` for why `maxTouchPoints` on its own is the wrong question.
   */
  const platform = readPlatformSignals();
  const touchTier = isTouchPrimary(platform);

  // --- quality ------------------------------------------------------------------------
  /*
   * A phone opens at Low and climbs; a desktop opens at Medium.
   *
   * Not pessimism — asymmetry. There is no way to ask a browser how fast its GPU is, so starting
   * low on a fast phone costs ten seconds of plainer sky before the governor steps up, whereas
   * starting high on a slow one costs a stuttering, thermally throttled first impression during
   * the exact ten seconds anyone decides whether to keep playing.
   */
  const quality = new QualityManager('auto', startTierFor(platform));
  quality.load();
  /*
   * Seed the crash report's tier now, not only when the tier next moves.
   *
   * `applyQuality` is the natural place to set this and it is not enough: a session where the
   * governor is content never calls it at all, so a crash on a perfectly happy machine reported
   * "tier: not yet chosen" — which is both untrue and exactly the field a reader would use to
   * decide whether the tier was implicated.
   */
  diagnostics.tier = `${quality.tier} (${quality.mode})`;
  // The settings screen is the authority on the tier; the stored quality key is what the governor
  // wrote last session, and the two must not disagree about what the player asked for.
  quality.setMode(settingsStore.settings.qualityMode);
  /*
   * `?tier=high` pins the tier before anything reads it.
   *
   * It exists because several features are tier-gated — cloud shadows, SSR, motion blur — and
   * there is otherwise no way to *measure* one: a perf run pins whatever tier the governor
   * happened to land on, which is Medium on the reference laptop, so a High-only effect is
   * measured at exactly zero cost and its budget is never actually tested. Applied after `load()`
   * so it wins over a saved preference, and it deliberately pins the mode too — an Auto governor
   * would step off the tier being measured within seconds.
   */
  const pinnedTier = requestedTier();
  if (pinnedTier !== null) quality.setMode(pinnedTier);
  const governor = new QualityGovernor(quality);

  // --- renderer -----------------------------------------------------------------------
  setStatus('initialising renderer…');
  const { renderer, backend, forcedFallback } = await createRenderer({
    canvas,
    settings: quality.settings,
  });
  console.info(`${PRODUCT_NAME} renderer: ${backend}${forcedFallback ? ' (forced via ?forcewebgl=1)' : ''}`);

  /*
   * `?rebase=` — the floating-origin threshold, for diagnosing periodic jitter. See
   * `requestedRebaseDistanceM`. Applied before the world exists so the very first rebase uses it.
   */
  const rebaseOverrideM = requestedRebaseDistanceM();
  if (rebaseOverrideM !== null) {
    setOriginRebaseDistanceM(rebaseOverrideM);
    console.info(
      `${PRODUCT_NAME} origin rebase: every ${originRebaseDistanceM()} m (overridden via ?rebase=) ` +
        `— diagnostic only; the shipped value is ${ORIGIN_REBASE_DISTANCE_M} m.`,
    );
  }
  diagnostics.backend = `${backend}${forcedFallback ? ' (forced via ?forcewebgl=1)' : ''}`;
  diagnostics.device = touchTier ? 'touch tier' : 'keyboard / mouse / gamepad';

  // --- aircraft -------------------------------------------------------------------------
  /*
   * Which aeroplane, and where the answer comes from.
   *
   * `?aircraft=vandal` wins, because that is what a bug report or a scripted run pins down; then
   * the last flight, which is what a returning pilot expects; then the trainer. The registry
   * refuses to throw on an unknown id — a bad URL opens the Sparrow rather than a blank page.
   */
  setStatus('loading aircraft…');
  rng.reseed('skysim-m2');
  const rememberedFlight = loadLastFlight(storage);
  const selected = aircraftById(requestedAircraftId() ?? rememberedFlight?.aircraftId);
  const aircraft = loadAircraft(selected.config);

  const engineArcs = engineReference(aircraft.config.engine);

  /*
   * The armament.
   *
   * Built from the config's `weapons` block, which the trainer and the aerobat do not have — so
   * for them this is a `WeaponSystem` with three null members that never fires, never draws and
   * never makes a sound. That is cheaper than a conditional everywhere and it is what makes
   * "free flight is weapons-cold" true by construction rather than by vigilance.
   */
  const weapons = new WeaponSystem(aircraft.config.weapons ?? {}, rng.fork());
  const weaponInput = makeWeaponInput();
  const hitQueue = new HitQueue();
  /** Body axes resolved into sim-frame NED, rebuilt each tick from the attitude. */
  const bodyForward = new Vector3();
  const bodyRight = new Vector3();
  const bodyDown = new Vector3();

  const atmosphere = new Atmosphere();
  const wind = new WindModel(rng.fork(), {
    layers: [
      { altitudeM: 0, directionDeg: 250, speedMps: 3 },
      { altitudeM: 900, directionDeg: 265, speedMps: 7 },
    ],
    gustSigmaMps: 1.1,
    turbulenceSigmaMps: 0.5,
  });

  const fdm = new FlightDynamicsModel(aircraft, { atmosphere, wind, groundElevationM: 0 });

  /*
   * Sound.
   *
   * Built here, before the renderer has drawn anything, because the graph has to exist before the
   * user's first gesture can start it — see `audio/context.ts` for why that ordering is the whole
   * autoplay story. Nothing is audible until that gesture; on a platform with no Web Audio at all
   * the system is a set of no-ops and the simulator is simply silent.
   *
   * Its sound set is derived from the aircraft's own physics config (cylinder count, propeller
   * diameter, the wing's real stall angle) rather than written down twice — see `audio/soundSet.ts`.
   */
  const audio = new AudioSystem({ soundConfig: soundConfigFor(aircraft) });
  /** Reused every frame: the camera has no velocity of its own, so it is differenced. */
  const listenerPrevious = new Vector3();
  const listenerRight = new Vector3();
  const audioVelocity = new Vector3();
  let listenerSeeded = false;

  /** Reused every frame: the flight HUD's extra state, filled in `afterFrame`. */
  const hudExtra = {
    onGround: true,
    stalled: false,
    crashed: false,
    coordination: { enabled: false, output: 0 },
  };

  /** Reused every frame: the camera rig is a frame-path object and must not allocate. */
  const motion = {
    velocityNed: new Vector3(),
    loadFactor: 1,
    onGround: true,
    heightAboveGroundM: 0,
  };
  const flapDetents = aircraft.config.controls.flapDetentsDeg;
  const maxFlapDeg = flapDetents[flapDetents.length - 1];

  /** Reused every tick: the assist is a control loop, and control loops must not allocate. */
  const coordinationFeedback: CoordinationFeedback = {
    betaRad: 0,
    yawRateRadPerS: 0,
    trueAirspeedMps: 0,
    onGround: true,
    yawDisturbanceNm: 0,
    rudderAuthorityNm: 0,
    rollRateRadPerS: 0,
    bankRad: 0,
  };

  // --- world and scene -------------------------------------------------------------------
  // The world starts at the default field so the first tiles are already streaming while the
  // airport database downloads; `spawnAt` re-anchors it the moment the database arrives.
  setStatus('building the world…');
  const startLatitudeDeg = requestedLatitude() ?? 37.6188;
  const startLongitudeDeg = requestedLongitude() ?? -122.3754;

  const scene = new FlightScene({
    settings: quality.settings,
    latitudeDeg: startLatitudeDeg,
    longitudeDeg: startLongitudeDeg,
    utc: requestedUtc() ?? undefined,
    weather: requestedWeather() ?? undefined,
    backend,
    // The exterior is the *aircraft's* too — a trainer, an aerobat and a fighter, and not three
    // copies of the trainer. See `render/airframes.ts`.
    aircraftId: aircraft.config.id,
    /*
     * Upload a tile's imagery the moment it is adopted, not in the frame that first draws it.
     *
     * `initTexture` costs ~0.21 ms here and takes about 3 ms off the ~10 ms that creating the
     * tile's `RenderObject` costs inside `render()`. Guarded because the WebGL2 fallback has the
     * method too but a failure to warm must never be fatal — the frame that draws the tile would
     * simply do the upload itself, which is what used to happen.
     */
    warmTexture: (texture) => {
      try {
        renderer.initTexture(texture);
      } catch {
        // Not fatal: the first draw uploads it, exactly as before this existed.
      }
    },
    // The instrument faces are the *aircraft's*: its own V-speeds and its own redline, so the
    // airspeed indicator's arcs mean something rather than merely looking about right.
    gauges: {
      airspeed: {
        vs0: aircraft.config.reference?.stallFullFlapKias ?? 40,
        vs1: aircraft.config.reference?.stallCleanKias ?? 48,
        vfe: aircraft.config.limits.vfeKias,
        vno: aircraft.config.limits.vnoKias ?? aircraft.config.limits.vneKias * 0.8,
        vne: aircraft.config.limits.vneKias,
        maximum: Math.ceil((aircraft.config.limits.vneKias * 1.1) / 10) * 10,
      },
      // The engine gauge's arcs come from the engine, whatever kind it is: revolutions per
      // minute on a piston, N2 per cent on a turbine. See `engineReference`.
      tachometer: {
        greenLow: engineArcs.speedGreenLow,
        greenHigh: engineArcs.speedGreenHigh,
        redline: engineArcs.speedRedline,
        maximum: engineArcs.speedGaugeMaximum,
      },
    },
    eyePointM: aircraft.config.eyePointM ?? [0.5, -0.35, -0.75],
    /*
     * What this aeroplane's cockpit looks like, derived from its own flight-model numbers.
     *
     * Until v1.1 there was one cockpit and it was the trainer's, so a pilot in the fly-by-wire
     * fighter flew it from behind a Cessna 172 panel with a control yoke, a fuel gauge reading
     * `US GAL` and a tachometer marked `RPM / HUNDREDS`. `cockpitProfileFor` reads the engine
     * kind and the negative-g limit and hands back a stick or a yoke, the right instrument set,
     * and a panel colour — so `cockpit.ts` still holds no per-aircraft branch.
     */
    cockpitProfile: cockpitProfileFor(aircraft.config),
  });
  scene.setEyePoint(aircraft.config.eyePointM ?? [0.5, -0.35, -0.75]);

  // --- combat: effects, the things you shoot at, and the view of both ------------------------
  /*
   * The effects layer is built whatever aircraft is loaded, because half of it is not about
   * weapons at all — contrails and wingtip vortices belong to any aeroplane that can get high
   * enough or pull hard enough, and the Tumbler can certainly do the second.
   */
  const effects = new CombatEffects({
    rng: rng.fork(),
    // Only an aircraft that can detonate something carries the flash light, because a light in
    // the scene is a light in every lit shader for the whole session. See `combatEffects.ts`.
    flashLight: weapons.hasWeapons,
  });
  scene.scene.add(effects.group);

  const combatView = new CombatView({
    effects,
    toRender: (positionNed, out) => scene.simNedToRender(positionNed, out),
  });
  scene.scene.add(combatView.group);
  if (weapons.missiles !== null) combatView.buildMissiles(weapons.missiles);

  /*
   * The reheat plume, parented to the aeroplane so it goes where the aeroplane goes.
   *
   * Only built for an aircraft that has an afterburner to light. Asking a 180 hp trainer to
   * render shock diamonds would be a draw call spent on nothing.
   *
   * **The exterior says where the nozzle is; this reads it.** It used to be the other way round —
   * a hard-coded `[0, 0.1, 4.6]` here, and both the built airframe and the baked glTF anchored to
   * land their nozzles on it, which cost the Vandal an origin 68 % aft along its airframe where an
   * F-16's centre of gravity is nearer 55 %. Two conditions on the aircraft's *config* and its
   * *geometry* were being resolved by moving the geometry; now they are separate. The gate is the
   * engine kind and the anchor is the airframe's, so a turbofan whose exterior declares no nozzle
   * gets no plume rather than one in an arbitrary place.
   */
  const afterburner =
    aircraft.config.engine.kind === 'turbofan' && scene.nozzle !== null
      ? new AfterburnerPlume({ nozzle: scene.nozzle, fullLengthM: 9 })
      : null;
  if (afterburner !== null) scene.aircraft.add(afterburner.group);

  /** Reused every frame: the wingtips, for contrails and vortices. */
  const leftTipRender = new Vector3();
  const rightTipRender = new Vector3();
  const effectsVelocity = new Vector3();
  const crashPosition = new Vector3();
  /** Last frame's crash flag, so the bang is edge-triggered. See the frame loop. */
  let wasCrashed = false;
  /** Reused: the bearing to the tracked target, for the HUD's designator box. */
  const toTargetNed = new Vector3();
  /**
   * Reused: how far the origin moved, handed to the flight model on a rebase.
   *
   * Preallocated like everything else on this path. A rebase is not every frame, but it is inside
   * the fixed-step block, and non-negotiable #3 does not have an "only sometimes" clause.
   */
  const originShiftNed = new Vector3();
  const halfSpanM = aircraft.config.geometry.wingSpanM * 0.5;

  const gunnery = new GunneryMode({
    rng: rng.fork(),
    storage,
    geodeticToNed: (latitudeDeg, longitudeDeg, altitudeM, out) =>
      scene.world.origin.frame.geodeticToNed(latitudeDeg, longitudeDeg, altitudeM, out),
  });
  /** What the weapons may hit. `NO_TARGETS` in free flight — see `engagement.ts`. */
  let targets: TargetList = NO_TARGETS;
  const weaponsHud = makeWeaponsHudState();

  /**
   * `?scene=canyon` — the scripted balloon-canyon run the M6 acceptance criterion names.
   *
   * Set up here and armed for real: the course is a real course, the gun is a real gun, and the
   * only thing scripted is the trajectory. Held at null in every other session.
   */
  /*
   * The rail only engages when a measurement is actually running.
   *
   * `?scene=canyon` on its own sets the course up at the canyon and lets you fly it. It is only
   * with `?perf=` as well that the aircraft goes on rails with the trigger held down — because
   * that combination is a *benchmark*, and a benchmark has to fly the same path every time.
   *
   * Splitting the two is not tidiness. Before it, opening the documented `?scene=canyon` URL by
   * hand handed you an aeroplane you could not fly, with a gun you could not stop and ammunition
   * that refilled itself, and no key that would end it. That is a fair description of a broken
   * simulator, and it is what the README was inviting people to try.
   */
  let canyonRun =
    requestedPerfScene() === 'canyon' && weapons.hasWeapons && requestedPerfSeconds() !== null
      ? new CanyonRun({
          headingDeg: BALLOON_COURSE_HEADING_DEG,
          groundElevationM: CANYON_COURSE.groundElevationM,
          anchorNed: gunnery.anchor,
        })
      : null;

  // `?time=` names a preset or gives a local solar hour; without it the default instant in
  // Environment applies. Done after construction so the preset solve sees the right latitude.
  if (requestedCockpitView()) scene.setCameraMode('cockpit');
  if (requestedRealNow()) scene.environment.setUtc(new Date());
  const requestedTime = requestedTimePreset();
  if (requestedTime !== null) scene.environment.setTimePreset(requestedTime);
  const requestedHour = requestedTimeHours();
  if (requestedHour !== null) scene.environment.setLocalSolarHours(requestedHour);
  scene.refreshEnvironment();

  /**
   * Push the environment's wind into the flight model.
   *
   * The weather owns one surface wind; `sim/atmosphere.ts` wants a layered profile, because the
   * wind aloft is neither the same speed nor the same direction as the wind on the runway — see
   * `world/weather.ts` for why. Rebuilt only when the weather changes, never per frame.
   */
  function applyWeatherToWind(): void {
    const weather = scene.environment.weather;
    wind.setLayers(windLayersFor(weather, scene.world.aircraftGeodetic.latitudeDeg));
    wind.gustSigmaMps = weather.gustMps;
    wind.turbulenceSigmaMps = weather.turbulenceMps;
    scene.refreshEnvironment();
  }

  /**
   * The buffet inside cloud.
   *
   * Cumulus is convective: it exists *because* air is going up through it, and the ride inside one
   * is measurably rougher than the air beside it. Rather than a canned shake this raises the
   * turbulence the flight model is already integrating, so the aeroplane is genuinely disturbed —
   * the wings rock, the ball moves, the altimeter wanders — and a firm hand on the controls damps
   * it out the way it would in the aeroplane. Scaled by the same immersion term that drives the
   * whiteout, so the two arrive together.
   *
   * Called once a frame; it writes two numbers and allocates nothing.
   */
  function applyCloudBuffet(): void {
    const weather = scene.environment.weather;
    const immersion = scene.clouds.cloudiness;
    wind.turbulenceSigmaMps = weather.turbulenceMps + immersion * 1.6;
    wind.gustSigmaMps = weather.gustMps + immersion * 0.8;
  }
  applyWeatherToWind();

  // The HDR chain. Built after the scene, because it captures the scene and camera it draws.
  const post = new PostChain({
    renderer,
    scene: scene.scene,
    camera: scene.camera,
    settings: quality.settings,
    /*
     * The volumetric layer is on wherever the tier asks for it — Medium and up. Which tiers those
     * are lives in `core/quality.ts`; nothing here needs an opinion.
     *
     * It is safe to leave wired even while the march is still returning clear air (see the status
     * note at the top of `clouds/volumetricClouds.ts`): the composite is `scene · transmittance +
     * scattering`, and a buffer of transmittance 1 and zero scattering is an exact no-op rather
     * than a black screen. Reach the buffer itself with `?debug=cloudalpha`, or get numbers out of
     * it with `await __SKYSIM__.probeClouds()` — do not judge it by eye, which is how the previous
     * attempt concluded the pass was running when it had never been built at all.
     */
    clouds: scene.clouds,
    debug: requestedDebugView(),
  });

  let viewport = resizeRenderer(renderer, canvas, quality.settings);
  scene.setViewport(viewport.widthPx, viewport.heightPx);

  /**
   * Size the HUD.
   *
   * It takes the *camera's* field of view as well as the viewport, because that is what makes the
   * symbology collimated — the pitch ladder has to sit on the real horizon at any field of view.
   * So this is called on a resize, on a field-of-view change, and on a camera change.
   */
  function resizeHud(): void {
    fighterHud.setViewport(
      canvas.clientWidth || viewport.widthPx,
      canvas.clientHeight || viewport.heightPx,
      scene.camera.fov,
      Math.min(2, globalThis.devicePixelRatio ?? 1),
    );
  }

  const resizeObserver = new ResizeObserver(() => {
    viewport = resizeRenderer(renderer, canvas, quality.settings);
    scene.setViewport(viewport.widthPx, viewport.heightPx);
    resizeHud();
    /*
     * Re-ask the orientation question here as well as on `resize`.
     *
     * Belt and braces, and it earns its keep: the window `resize` event is not reliably fired for
     * every way a viewport can change shape — an Android split-screen drag and an embedded frame
     * being resized by its host both change the canvas box without one, and this observer sees
     * exactly that. Measured: a programmatic viewport change from portrait to landscape left the
     * prompt up until a `resize` was dispatched by hand.
     *
     * `refreshOrientation` is a hoisted function declaration whose captured `const`s are
     * initialised further down in this same synchronous run, and an observer callback cannot be
     * delivered until after it — the next `await` is the shader pre-compile at the very end.
     */
    refreshOrientation();
  });
  resizeObserver.observe(canvas);

  // --- input ------------------------------------------------------------------------------
  const keyboard = new Keyboard();
  keyboard.attach();
  const pointer = new PointerControls(canvas);
  pointer.attach();

  const controls = new FlightControls({
    flapDetentCount: aircraft.config.controls.flapDetentsDeg.length,
  });
  controls.bind(keyboard);
  controls.loadPreferences();

  globalThis.addEventListener('pointermove', (event: PointerEvent) => {
    if (!controls.isMouseYokeEnabled) return;
    const x = (event.clientX / globalThis.innerWidth) * 2 - 1;
    const y = (event.clientY / globalThis.innerHeight) * 2 - 1;
    controls.setMouseYoke(x, y);
  });

  /*
   * --- the touch tier -------------------------------------------------------------------------
   *
   * Built only on a device that is actually flown with a thumb, and `null` everywhere else — so a
   * desktop session carries no extra DOM, no extra listeners, and nothing in the command layer's
   * hot path but one null check per tick.
   *
   * The buttons call the same functions the keys do rather than synthesising key events: a
   * synthetic `KeyboardEvent` would go through `Keyboard`'s listener, the menu's capture-phase
   * handler and the rebinding layer, and each of those is a place for the two paths to diverge.
   */
  const touchControls = touchTier
    ? new TouchControls({
        /*
         * The same lever the G key moves, and it used to be a lie.
         *
         * This printed "this aeroplane has fixed gear" unconditionally — true of the Sparrow and
         * the Tumbler, whose real counterparts do not retract, and simply false on the Vandal.
         * It now moves the gear and reports what happened, and only claims fixed gear when the
         * loaded aircraft actually has it.
         */
        onGear: () => {
          if (aircraft.config.gear.retractable === undefined) {
            setPill('this aeroplane has fixed gear', 2_000);
            return;
          }
          setPill(controls.toggleGear() ? 'gear down' : 'gear up', 2_000);
        },
        onFlaps: () => controls.cycleFlaps(1),
        onCamera: () => {
          scene.cycleCameraMode();
          settingsStore.patch({ cameraMode: scene.cameraMode });
        },
        onPause: () => {
          loop.pause();
          menu.setScreen('pause');
        },
      })
    : null;
  controls.setTouchSource(touchControls);

  /*
   * The landscape gate.
   *
   * Pauses the simulation behind it rather than letting it run unseen: the pilot cannot fly while
   * it is up, and a simulator that keeps flying for the ten seconds it takes to turn a phone hands
   * back an aeroplane that has wandered off somewhere.
   */
  const landscapePrompt = touchTier
    ? new LandscapePrompt({
        onChange: (showing) => {
          touchControls?.setVisible(!showing);
          if (showing) loop.pause();
          else if (!menu.isOpen) loop.resume();
        },
      })
    : null;

  /** Re-ask the orientation question. Cheap, and only ever called on a resize. */
  function refreshOrientation(): void {
    landscapePrompt?.setShowing(
      shouldPromptForLandscape(platform, globalThis.innerWidth, globalThis.innerHeight),
    );
  }

  if (touchTier) {
    /*
     * One class on `<body>`, so the stylesheet can move the overlays that collide with a thumb.
     *
     * Today that is the camera button, whose desktop home in the bottom-right corner is exactly
     * where the throttle lever lives — they were measured in complete overlap. A class rather than
     * a media query because the condition is "flown with a thumb", which is three signals and a
     * URL override (`core/platform.ts`), not a viewport width: a phone in landscape is 844 px wide
     * and a narrow desktop window is not a phone.
     */
    document.body.classList.add('skysim-touch');
    /*
     * Two listeners rather than the canvas `ResizeObserver` above, and both of them: `resize` is
     * what fires reliably when a phone is turned, and `orientationchange` fires *before* the
     * viewport has settled on some browsers — so the second is a belt-and-braces re-ask rather
     * than the primary signal.
     */
    globalThis.addEventListener('resize', refreshOrientation);
    globalThis.addEventListener('orientationchange', refreshOrientation);
    refreshOrientation();
    touchControls?.setVisible(landscapePrompt?.showing !== true);
  }

  // --- spawning -----------------------------------------------------------------------------
  /** Where the last spawn put us, so `R` can put us back there. */
  let lastSpawn: SpawnChoice | null = null;

  /*
   * Landing scoring — see `game/modes/landing.ts`.
   *
   * Armed against the runway the pilot spawned at, which is the one they are almost certainly
   * coming back to. Scoring an arrival at an airport nobody declared would mean searching the
   * database every tick for whatever is underneath, and being wrong about it near a field with
   * parallel runways.
   */
  const landingRecords = new LandingRecordKeeper(storage);
  landingRecords.load();
  let landingScorer: LandingScorer | null = null;
  let landingRunway: (RunwayEnds & { icao: string; designator: string; lengthM: number; widthM: number }) | null = null;
  /** Reported once per approach. A separate flag, not `scorer.completed === null` — see below. */
  let landingReported = false;

  /** Arm a fresh scorer for the runway just spawned at. Called on every spawn and respawn. */
  function armLandingScorer(choice: SpawnChoice): void {
    const runway = choice.runway;
    /*
     * An `inferred` runway sits on the airport's reference point rather than on surveyed
     * thresholds — see `world/airports/format.ts`. Scoring a touchdown against geometry that is a
     * guess would hand out a number with no meaning, which is worse than handing out none.
     */
    if (runway.placement !== PLACEMENT.surveyed) {
      landingScorer = null;
      landingRunway = null;
      return;
    }
    const landingLow = choice.end === 'low';
    landingRunway = {
      icao: choice.icao,
      designator: landingLow ? runway.lowIdent : runway.highIdent,
      thresholdLatitudeDeg: landingLow ? runway.lowLatitudeDeg : runway.highLatitudeDeg,
      thresholdLongitudeDeg: landingLow ? runway.lowLongitudeDeg : runway.highLongitudeDeg,
      thresholdElevationM: landingLow ? runway.lowElevationM : runway.highElevationM,
      headingDegTrue: landingLow ? runway.headingDegTrue : (runway.headingDegTrue + 180) % 360,
      lengthM: runway.lengthM,
      widthM: runway.widthM,
    };
    landingScorer = new LandingScorer({ lengthM: runway.lengthM, widthM: runway.widthM });
    landingReported = false;
  }

  /**
   * Put the aircraft somewhere on the planet.
   *
   * The order matters: the world is re-anchored *first*, so the aircraft's local coordinates
   * come out small and the terrain streamer starts on the right tiles immediately, rather than
   * briefly asking for tiles half a continent away.
   */
  function spawnAt(choice: SpawnChoice): void {
    lastSpawn = choice;
    armLandingScorer(choice);
    // Remember where we are, so the next session's FLY button opens here rather than at KSFO.
    saveLastFlight(storage, {
      aircraftId: aircraft.config.id,
      icao: choice.icao,
      runwayEnd: choice.end,
      runwayIdent: choice.end === 'low' ? choice.runway.lowIdent : choice.runway.highIdent,
      mode: choice.mode,
    });
    const pose =
      choice.mode === 'final'
        ? finalApproachSpawn(choice.runway, choice.end, requestedFinalDistanceNm())
        : thresholdSpawn(choice.runway, choice.end, fdm.staticGroundClearanceM);

    scene.world.teleport(pose.latitudeDeg, pose.longitudeDeg);
    scene.world.showAirport(choice.icao);
    // The sky depends on where you are: spawning at Innsbruck from San Francisco has to move
    // the sun, not just the terrain.
    scene.setObserver(pose.latitudeDeg, pose.longitudeDeg);

    const ned = scene.world.origin.frame.geodeticToNed(
      pose.latitudeDeg,
      pose.longitudeDeg,
      pose.altitudeM,
      new Vector3(),
    );

    fdm.body.positionNed.copy(ned);
    fdm.body.angularVelocityBody.set(0, 0, 0);
    eulerToQuaternion(pose.headingDegTrue * DEG2RAD, 0, 0, fdm.body.attitude);

    if (choice.mode === 'final') {
      // Trimmed for a stabilised approach rather than dropped out of the sky at zero airspeed.
      const approachSpeedMps = 33;
      fdm.body.velocityNed.set(
        Math.cos(pose.headingDegTrue * DEG2RAD) * approachSpeedMps,
        Math.sin(pose.headingDegTrue * DEG2RAD) * approachSpeedMps,
        approachSpeedMps * Math.tan(3 * DEG2RAD),
      );
      // A respawn hands back a *running* aeroplane, whatever the last session did with the mags.
      // In its own units: rpm on a piston, N2 per cent on a turbine. Handing a turbofan 1900
      // would command 1900 % N2, which is the sort of thing non-negotiable #8 exists to prevent.
      aircraft.propulsion.forceRunning(engineIdleSpeed(aircraft.config.engine) * 2.4);
      controls.reset();
      controls.cycleFlaps(1);
    } else {
      fdm.body.velocityNed.set(0, 0, 0);
      aircraft.propulsion.forceRunning(engineIdleSpeed(aircraft.config.engine));
      controls.reset();
    }

    aircraft.gear.reset();
    fdm.resetDamage();
    // The on-screen lever is a physical thing that stays where it is put, so a respawn has to move
    // it — otherwise the next tick pushes the throttle straight back to wherever it was left.
    touchControls?.setThrottle(controls.throttle);
    /*
     * Forget the tile failures the last location accumulated.
     *
     * A spawn is a teleport, and a new region legitimately misses tiles at the edge of what its
     * sources publish. Carrying the old count across would let two unrelated regions' misses add
     * up into a "the terrain source is down" banner about a source that is answering fine.
     */
    connection.resetFailureWindow(performance.now());
    // Seed the ground elevation with the *threshold's*, not the spawn point's. Deriving it
    // from the spawn altitude works on the runway and is badly wrong on final, where it put
    // the ground 300 m up and reported the aircraft as sitting on it at 900 ft.
    fdm.groundElevationM =
      choice.end === 'low' ? choice.runway.lowElevationM : choice.runway.highElevationM;
    fdm.syncPose();
    scene.cameraRig.reset();
    scene.setCameraMode(scene.cameraMode);
    readInstruments(fdm.state, instrumentInputs);
    scene.cockpit.snap(instrumentInputs);
    setPill(`${choice.icao} · runway ${choice.end === 'low' ? choice.runway.lowIdent : choice.runway.highIdent}`, 4_000);
  }

  const spawnPicker = new SpawnPicker({
    onSpawn: (choice) => {
      spawnPicker.setVisible(false);
      spawnAt(choice);
    },
  });

  /*
   * `?allocprobe=1` — attribute the frame's allocation to the channel that caused it.
   *
   * Off unless asked for: it reads the heap at every channel boundary, which is cheap but not
   * free, and a diagnostic that is always on is a diagnostic that is part of the measurement.
   */
  const allocationProbe = requestedAllocationProbe() ? new AllocationProbe() : null;

  // --- loop ---------------------------------------------------------------------------------
  const loop = new GameLoop({
    ...(allocationProbe === null ? {} : { allocationProbe }),
    fixedUpdate: (dt) => {
      // The assist reads what the airframe did last tick. One tick of lag on a control loop
      // running at 120 Hz is 8 ms, which is well inside the aeroplane's own response time.
      coordinationFeedback.betaRad = fdm.state.betaRad;
      coordinationFeedback.yawRateRadPerS = fdm.body.angularVelocityBody.z;
      coordinationFeedback.rollRateRadPerS = fdm.body.angularVelocityBody.x;
      coordinationFeedback.bankRad = fdm.state.rollRad;
      coordinationFeedback.trueAirspeedMps = fdm.state.trueAirspeedMps;
      coordinationFeedback.onGround = fdm.gear.onGround;
      // The feed-forward half of the assist: what the propeller is doing to the yaw axis right
      // now, and what a unit of rudder is worth against it. See `input/coordination.ts`.
      coordinationFeedback.yawDisturbanceNm = fdm.yawBalance.disturbanceNm;
      coordinationFeedback.rudderAuthorityNm = fdm.yawBalance.rudderAuthorityNm;

      // The starter is a *hold*, so it is sampled every tick rather than bound to a press. It is
      // refused while the engine is already running, which is what makes `I` unambiguous: it
      // cranks a dead engine and shuts down a live one, exactly like the key it is named after.
      controls.setStarter(keyboard.isDown('KeyI') && !fdm.state.engineRunning);

      const commands = controls.updateFixed(keyboard, dt, coordinationFeedback);
      fdm.updateFixed(commands, dt);

      // --- weapons ---------------------------------------------------------------------------
      /*
       * Resolve the body axes into sim-frame NED once, here, and hand them to everything that
       * needs them. The gun's muzzle, the missile rails, the dispersion basis and the gun sight
       * all want the same three vectors, and rotating them four times would be three times too
       * many — see `weaponSystem.ts` on why the axes cross the boundary rather than a quaternion.
       */
      if (weapons.hasWeapons) {
        bodyForward.set(1, 0, 0).applyQuaternion(fdm.body.attitude);
        bodyRight.set(0, 1, 0).applyQuaternion(fdm.body.attitude);
        bodyDown.set(0, 0, 1).applyQuaternion(fdm.body.attitude);

        weaponInput.positionNed.copy(fdm.body.positionNed);
        weaponInput.forwardNed.copy(bodyForward);
        weaponInput.rightNed.copy(bodyRight);
        weaponInput.downNed.copy(bodyDown);
        weaponInput.velocityNed.copy(fdm.body.velocityNed);
        weaponInput.pitchRateRadPerS = fdm.body.angularVelocityBody.y;
        weaponInput.yawRateRadPerS = fdm.body.angularVelocityBody.z;
        weaponInput.densityKgPerM3 = fdm.atmosphereSample.densityKgPerM3;
        /*
         * `ground === ground` rather than `Number.isNaN(ground)`.
         *
         * Passing an unboxed double to the builtin forces V8 to materialise it as a HeapNumber —
         * the same artefact `core/profiler.ts` documents at its `NOT_TIMING` sentinel. This runs
         * on the 120 Hz tick, and self-comparison is the canonical NaN test and compiles to a
         * single machine comparison.
         */
        const groundM = scene.world.groundElevationM;
        weaponInput.groundElevationM = groundM === groundM ? groundM : 0;
        /*
         * The trigger. Held down for the whole scripted run — a hundred rounds a second, four
         * hundred alive at once and eighty tracer ribbons is the worst case the effects budget
         * has to survive, and a benchmark that measured anything gentler would be measuring the
         * wrong thing.
         */
        weaponInput.trigger =
          keyboard.isDown('Space') || controls.touchFire || canyonRun !== null;
        /*
         * A scripted run rearms itself.
         *
         * 500 rounds at 100 a second is five seconds of gun, so without this the "gun held down"
         * worst case applied to the first eighth of a 40 s window and the rest measured a silent
         * empty aeroplane. Rearming keeps four hundred rounds in the air for the whole
         * measurement, which is the load the budget is supposed to survive.
         */
        if (canyonRun !== null && weapons.cannon !== null && weapons.cannon.empty) {
          weapons.cannon.rearm();
        }

        const ammunitionBefore = weapons.cannon?.ammunition ?? 0;
        hitQueue.clear();
        weapons.clearEvents();
        gunnery.targets.clearEvents();
        weapons.updateFixed(dt, weaponInput, targets, hitQueue);
        gunnery.updateFixed(dt, weapons, hitQueue);
        reportWeaponSounds(ammunitionBefore);

        /*
         * What is left aboard, back into the flight model.
         *
         * Only when a count moved — `storesChanged` returns null otherwise — because the response
         * inverts an inertia tensor and the gun fires a hundred times a second. Firing everything
         * takes 391 kg off a 12-tonne aeroplane, most of it from the wingtips, and both the roll
         * inertia and the trim change with it. See `weapons/stores.ts`.
         */
        const stores = weapons.storesChanged();
        if (stores !== null) fdm.setStores(stores);
      }

      /*
       * Score the landing, if one is happening.
       *
       * On the fixed step rather than the frame, because touchdown is a one-tick event and the
       * sink rate that defines it is gone by the next frame. The projection is a handful of
       * multiplies against a position the world already computed this tick.
       */
      /*
       * Guarded on `landingReported` rather than on `scorer.completed === null`, because the
       * latter narrows the getter's type to `never` for the rest of the block — the compiler has
       * no way to know that `sample()` below is what makes it non-null.
       */
      if (landingScorer !== null && landingRunway !== null && !landingReported) {
        const here = scene.world.aircraftGeodetic;
        const frame = projectToRunway(landingRunway, {
          latitudeDeg: here.latitudeDeg,
          longitudeDeg: here.longitudeDeg,
          altitudeM: fdm.state.altitudeM,
        });
        landingScorer.sample({
          alongM: frame.alongM,
          crossM: frame.crossM,
          heightM: frame.heightM,
          groundSpeedMps: fdm.state.groundSpeedMps,
          onGround: fdm.state.onGround,
          sinkRateMps: -fdm.state.verticalSpeedMps,
        });
        const landed = landingScorer.completed;
        if (landed !== null) {
          landingReported = true;
          const improved = landingRecords.submit(
            landingRunway.icao,
            landingRunway.designator,
            landed.score,
            landed.grade,
          );
          setPill(
            `${landed.grade} · ${landed.score}/100 on ${landingRunway.designator}` +
              `${improved ? ' · best yet' : ''}`,
            8_000,
          );
          console.info(
            `${PRODUCT_NAME} landing: ${landingRunway.icao}/${landingRunway.designator} ` +
              `${landed.score}/100 (${landed.grade}) — touchdown ${landed.touchdownAlongM.toFixed(0)} m, ` +
              `${landed.touchdownSinkRateMps.toFixed(2)} m/s, ` +
              `threshold ${landed.thresholdHeightM === null ? 'n/a' : `${landed.thresholdHeightM.toFixed(0)} m`}`,
          );
        }
      }

      /*
       * The scripted benchmark flight, if one is running.
       *
       * *After* the flight model, deliberately: the physics tick has already happened and been
       * charged for, and only the resulting trajectory is replaced. See `game/canyonRun.ts` for
       * why a benchmark's path has to be a rail rather than an autopilot.
       */
      if (canyonRun !== null) {
        canyonRun.updateFixed(
          dt,
          fdm.body.positionNed,
          fdm.body.velocityNed,
          fdm.body.attitude,
          scene.world.groundElevationM,
        );
        fdm.syncPose();
      }

      /*
       * Remember where the aeroplane was before the origin gets a chance to move.
       *
       * Everything anchored to the planet re-derives itself after a rebase, but particles have no
       * latitude and longitude — a contrail is just a list of points — so they have to be
       * translated by hand. This is the translation, and it has to be sampled *before* the call
       * below zeroes the position it is computed from.
       */
      const preRebaseNorthM = fdm.body.positionNed.x;
      const preRebaseEastM = fdm.body.positionNed.y;

      const rebased = scene.world.updateFixed(
        fdm.body.positionNed.x,
        fdm.body.positionNed.y,
        fdm.body.positionNed.z,
      );
      if (rebased) {
        // Render space is x east, y up, z south — hence the sign on the north component.
        effects.applyOriginShift(-preRebaseEastM, 0, preRebaseNorthM);
        /*
         * The camera rig, which holds world positions between frames for three of its five views —
         * the chase camera's smoothed follow point, the fly-by tripod, and the drone. Uncarried,
         * all three are dragged along by the origin: the chase filter sees the shift as a step and
         * spends half a second reeling itself back in, every 1.9 s at 500 kt.
         */
        scene.cameraRig.applyOriginShift(-preRebaseEastM, 0, preRebaseNorthM);
        /*
         * Ordnance in the air, in sim-frame NED this time rather than render space.
         *
         * Rounds and missiles are integrated in place and have no latitude and longitude, so they
         * are left in the old frame unless they are moved — while gunnery targets re-derive from a
         * geodetic anchor every tick and the terrain re-derives its tiles. Miss this and the gun
         * and the things it is shooting at are simply in different frames.
         *
         * It was survivable at a 10 km threshold, where a rebase came every 39 s against a round
         * lifetime of four seconds. At 500 m the period is 1.9 s — shorter than the rounds live —
         * so it stopped being a rare cosmetic error and started affecting practically every burst.
         */
        weapons.applyOriginShift(-preRebaseNorthM, -preRebaseEastM, 0);
      }
      if (rebased) {
        // The aircraft *is* the new origin, horizontally. Its altitude is unchanged, because
        // the origin only ever moves along the surface — see geodesy.ts.
        fdm.body.positionNed.x = 0;
        fdm.body.positionNed.y = 0;
        /*
         * The interpolation history, carried rather than discarded — the fourth consumer of
         * render-continuity across a rebase, and the one M7 part 2 missed.
         *
         * This was `fdm.syncPose()`, which snaps `previousPositionNed` to the current pose. That
         * is right for a respawn, where the aeroplane genuinely has no past, and wrong here, where
         * the aeroplane has not moved at all and only the coordinate system has. Snapping makes
         * `previous` and `current` equal, so `samplePose(alpha)` returns the raw tick pose for
         * every alpha and the rendered aeroplane skips forward by whatever fraction of a tick the
         * interpolation was mid-way through.
         *
         * Measured at 400 kt with a 60 fps display: a **2.075 m lurch, 51.5 % of one frame's
         * 4.03 m of motion**, on every rebase — seven of them in a fifteen-second run, one every
         * 2.1 s. Shifting the history instead leaves 0.00087 m, which is 0.02 %. That is the
         * pilot's "the aircraft visibly jitters sometimes during flight", and *sometimes* was the
         * clue: a fixed 120 Hz tick interpolated to the display is smooth by construction, so a
         * periodic disturbance had to come from something periodic, and the only thing on that
         * cadence is the 500 m rebase.
         *
         * It was missed in M7 part 2 because that audit went looking for *render-space* state, and
         * this is sim-frame NED. Pinned by `sim/rebaseInterpolation.test.ts`.
         */
        originShiftNed.set(-preRebaseNorthM, -preRebaseEastM, 0);
        fdm.applyOriginShift(originShiftNed);
        /*
         * A rebase is 500 m of travel — about 0.006° of longitude at mid-latitudes, or a second or
         * two of hour angle. That is nothing on its own, and it is not why this call is here: over
         * a long flight the shifts accumulate into a sun that is visibly in the wrong place, so the
         * observer moves with the origin. `setObserver` early-outs below ~11 m of movement, so
         * doing this twenty times more often than the old threshold did costs nothing measurable.
         */
        scene.setObserver(
          scene.world.aircraftGeodetic.latitudeDeg,
          scene.world.aircraftGeodetic.longitudeDeg,
        );
      }

      // `ground === ground` rather than `Number.isNaN` — see the note in the weapons block above.
      const ground = scene.world.groundElevationM;
      if (ground === ground) fdm.groundElevationM = ground;
    },
    // Terrain selection and tile adoption, in the slot the profiler budgets 2 ms for. It used
    // to run inside `frameUpdate`, which meant the streaming cost was reported as render CPU
    // and the terrain channel read a reassuring and entirely fictional 0.00 ms.
    streamUpdate: () => {
      scene.updateStreaming();
    },
    frameUpdate: (alpha, dt) => {
      controls.updateFrame(dt);
      applyCloudBuffet();

      if (!controls.isMouseYokeEnabled) {
        scene.cameraRig.orbit(pointer.drainYaw(), pointer.drainPitch());
      } else {
        pointer.drainYaw();
        pointer.drainPitch();
      }
      scene.cameraRig.dolly(pointer.drainDolly());

      const pose = fdm.samplePose(alpha);
      motion.velocityNed.copy(fdm.body.velocityNed);
      motion.loadFactor = fdm.state.loadFactor;
      motion.onGround = fdm.gear.onGround;
      motion.heightAboveGroundM = fdm.state.heightAboveGroundM;
      scene.updateFrame(pose.positionNed, pose.attitude, pose.propAngleRad, dt, motion);
      /*
       * The two things about the exterior that the flight model owns.
       *
       * Both were already computed and neither was being drawn: `gearFraction` carries the
       * aircraft's own retraction time and drives the gear drag, and `rpm` decides whether the
       * propeller is a set of blades or the disc a camera would record. Pushing them every frame
       * costs two assignments and a short loop, and it is what makes the picture and the physics
       * the same aeroplane.
       */
      scene.setGearFraction(fdm.state.gearFraction);
      scene.setShaftSpeed(fdm.state.rpm);

      // The cockpit's levers and yoke follow the *commands*, assist included — the pedals show
      // what the aeroplane is actually being flown with, not only what the pilot asked for.
      const cockpitControls = scene.cockpitControls;
      cockpitControls.pitch = controls.commands.pitch;
      cockpitControls.roll = controls.commands.roll;
      cockpitControls.yaw = controls.commands.yaw;
      cockpitControls.throttle = controls.commands.throttle;
      cockpitControls.flapAngleDeg = fdm.flapAngle;
      cockpitControls.maxFlapDeg = maxFlapDeg;

      const nowMs = performance.now();
      readInstruments(fdm.state, instrumentInputs);
      scene.updateCockpit(nowMs, instrumentInputs, dt);

      // --- combat, effects and the reheat plume -----------------------------------------------
      combatView.updateFrame(dt, gunnery.targets.all, weapons.missiles, weapons.cannon, hitQueue);

      /*
       * Contrails and vortices, from the wingtips.
       *
       * The tips are the aircraft's own transform applied to ±half the span, which is why this is
       * done after `scene.updateFrame` has placed it: taking the tips from the flight model
       * instead would put them where the aeroplane was at the last physics tick rather than where
       * it is being drawn, and the trail would visibly lag the wing.
       */
      leftTipRender.set(-halfSpanM, 0.6, 0).applyMatrix4(scene.aircraft.matrixWorld);
      rightTipRender.set(halfSpanM, 0.6, 0).applyMatrix4(scene.aircraft.matrixWorld);
      aeroToRenderVector(fdm.body.velocityNed, effectsVelocity);
      effects.contrail(
        leftTipRender,
        rightTipRender,
        effectsVelocity,
        fdm.state.altitudeM,
        fdm.state.throttle,
        dt,
      );
      effects.wingtipVortices(
        leftTipRender,
        rightTipRender,
        effectsVelocity,
        fdm.state.loadFactor,
        fdm.state.alphaRad,
        scene.environment.weather.precipitation > 0 ? 0.65 : 0.3,
        dt,
      );
      /*
       * The crash, once, on the frame it happens.
       *
       * Edge-triggered off `damage.crashed` rather than polled, because the flag stays set until
       * the pilot presses R and a level-triggered version would light a new fireball every frame
       * until they did. `scene.aircraft`'s world position is used rather than the flight model's,
       * so the bang goes off exactly where the aeroplane is *drawn* — the two differ by one
       * interpolation step, which at impact speed is metres.
       */
      if (fdm.damage.crashed !== wasCrashed) {
        wasCrashed = fdm.damage.crashed;
        if (wasCrashed) {
          scene.aircraft.getWorldPosition(crashPosition);
          effects.explosion(crashPosition, 1.6);
          audio.explosion(0);
        }
      }

      effects.updateFrame(dt);

      afterburner?.updateFrame(
        fdm.state.afterburnerFraction,
        fdm.atmosphereSample.pressurePa / 101_325,
      );

      if (fighterHud.visible) {
        readHud(fdm.state, hudInputs, hudLimits);
        refreshWeaponsHud();
        fighterHud.updateFrame(nowMs, hudInputs);
      }
    },
    audioUpdate: (dt) => {
      readAudio(dt);
      audio.updateFrame(dt);
    },
    render: () => {
      // Exposure is pushed here rather than in `frameUpdate` so it is the value the environment
      // settled on this frame, not last frame's.
      post.setExposure(scene.environment.exposure);
      if (!post.render()) renderer.render(scene.scene, scene.camera);
    },
    afterFrame: () => {
      const nowMs = performance.now();
      const info = renderer.info.render;

      screenshotRun?.updateFrame(nowMs);
      /*
       * The shutter, and it has to be *here*.
       *
       * This slot runs synchronously after `render()` and before the browser composites, which is
       * the only window in which a canvas without `preserveDrawingBuffer` can be read back. A
       * capture requested from the button or from key `6` is taken on the next pass through this
       * line, one frame after it was asked for.
       */
      shareController.updateFrame();
      shareButton.updateFrame(nowMs);
      /*
       * The camera button is for a pilot, so it is not on screen when there is not one: a menu is
       * up, or a scripted run owns the session. `setVisible` is idempotent, so asserting it every
       * frame costs one boolean comparison and saves listening for four separate transitions.
       */
      shareButton.setVisible(!menu.isOpen && perfHarness === null && screenshotRun === null);
      // The on-screen controls go with it: a stick over a menu would fly the aeroplane behind it.
      touchControls?.setVisible(!menu.isOpen && landscapePrompt?.showing !== true);

      perfOverlay.setRenderStats(info.drawCalls, info.triangles, viewport.widthPx, viewport.heightPx);
      perfOverlay.setTerrainStats(scene.world.terrain.statistics());
      perfOverlay.updateFrame(nowMs, loop.scheduler.totalTicks);
      /*
       * Filled rather than rebuilt. Two object literals a frame is not much, but `FlightHud`
       * throttles itself to 10 Hz and returns immediately — so nine in ten of them were being
       * constructed for a call that does nothing at all.
       */
      hudExtra.onGround = fdm.gear.onGround;
      hudExtra.stalled = fdm.state.stallWarning;
      hudExtra.crashed = fdm.damage.crashed;
      hudExtra.coordination.enabled = controls.diagnostics.coordinationEnabled;
      hudExtra.coordination.output = controls.diagnostics.coordinationOutput;
      hud.updateFrame(nowMs, fdm.state, hudExtra);

      const decision = governor.update(
        nowMs,
        loop.profiler.lowFps(),
        loop.profiler.achievableFps,
      );
      if (decision !== 'none') applyQuality('governor');

      // The camera's shake is scaled by how hard the arrival was, and the gear is what knows.
      scene.setTouchdownRate(fdm.gear.touchdownRateMps);
      menu.updateFrame(controls.gamepadState, 1 / 60);

      helpFooter.setActive('yoke', controls.isMouseYokeEnabled);
      helpFooter.setActive('coord', controls.diagnostics.coordinationEnabled);
      helpFooter.setActive('starter', fdm.state.starterEngaged || !fdm.state.engineRunning);

      environmentPanel.updateFrame(nowMs);
      const moon = scene.nightSky.moonPosition;
      if (moon !== null) environmentPanel.setMoon(moon.illuminatedFraction, moon.elevationDeg);

      /*
       * Is the world still reaching us?
       *
       * Four times a second, because the monitor's windows are ten and fifteen seconds long and a
       * per-frame poll would be a hundred and twenty reads of `navigator.onLine` for a decision
       * that cannot change that fast. The banner is only touched when the status actually moves.
       */
      if (nowMs >= connectionNextCheckMs) {
        connectionNextCheckMs = nowMs + 250;
        const status = connection.updateFrame(
          nowMs,
          scene.world.terrain.statistics().failed,
          globalThis.navigator?.onLine ?? true,
        );
        if (status !== publishedConnection) {
          publishedConnection = status;
          const message = connectionMessage(status);
          if (message === null) notices.clear('connection');
          else {
            notices.show({
              id: 'connection',
              text: message,
              // Above the update prompt: an outage is happening now, a new build can wait.
              priority: 20,
              tone: 'warn',
              // Dismissible, because a pilot who is deliberately flying offline has read it once
              // and should not have it across the top of every subsequent screenshot.
              dismissible: true,
            });
          }
        }
      }

      perfHarness?.updateFrame(nowMs, info.drawCalls, info.triangles, compiledProgramCount());
      updateStatusPill(nowMs);
    },
  });

  // --- overlays -------------------------------------------------------------------------------
  const perfOverlay = new PerfOverlay({
    profiler: loop.profiler,
    quality,
    governor,
    backend,
    forcedFallback,
  });
  const hud = new FlightHud();
  // A phone gets the six readouts it flies by; a desktop gets the flight-model instruments too.
  hud.setCompact(touchTier);
  const helpFooter = new HelpFooter();

  /*
   * The banner strip, and the two things allowed to use it.
   *
   * `ConnectionMonitor` watches `navigator.onLine` *and* the terrain's own failure count, because
   * the two catch different outages — see `core/connectivity.ts`. Polled at 4 Hz below rather than
   * per frame: its windows are measured in seconds and nothing here needs to be told sooner.
   */
  const notices = new NoticeBanner();
  const connection = new ConnectionMonitor();
  /** Last status published to the banner, so an unchanged status touches nothing. */
  let publishedConnection: ReturnType<ConnectionMonitor['updateFrame']> = 'online';
  let connectionNextCheckMs = 0;

  /*
   * The share loop.
   *
   * The controller does the work and the button only asks for it, because the readback cannot
   * happen in a click handler — a WebGPU canvas is only readable inside the frame that drew it.
   * See `game/screenshot.ts`; `shareController.updateFrame()` in the after-frame slot below is
   * the half that actually takes the picture.
   */
  const shareController = new ShareController({
    canvas,
    // Where we are, for the file name and the post line: the ICAO of the last spawn, which is
    // null until the airport database lands and the aircraft is placed.
    place: () => lastSpawn?.icao ?? null,
    /*
     * What a shared link opens into. Airport and aeroplane only, deliberately.
     *
     * `SharedFlight` also carries `time` and `weather`, and this does not supply them: neither
     * preset survives being set. `Environment.setTimePreset` solves for a sun *elevation* and then
     * the clock keeps running; `setWeatherPreset` converts to a `WeatherState` the weather sliders
     * then patch field by field. Neither keeps the name, and reconstructing one would be a link
     * that claims a sky the sharer is no longer under. Where and what are exact, and they are the
     * two that carry the meaning — "this aeroplane, out of this airport".
     */
    flight: () => ({ icao: lastSpawn?.icao ?? null, aircraft: aircraft.config.id }),
    onResult: (outcome, detail) => shareButton.report(outcome, detail),
  });
  const shareButton = new ShareButton({
    onCapture: () => shareController.request(),
    shareText: () => shareController.shareLine,
    onReshare: () => void shareController.reshare(),
    onSave: () => shareController.save(),
  });

  // --- cockpit, HUD and the shell --------------------------------------------------------------
  /** Reused every frame: the instruments and the HUD are frame-path objects. */
  const instrumentInputs = makeInstrumentInputs();
  const hudInputs = makeHudInputs();
  const fighterHud = new FighterHud();
  /*
   * The AoA bracket's scale is the aircraft's own. Stall alpha comes from the wing's table; the
   * approach alpha is the one at 1.3·Vs, which is the speed every light-aircraft approach is flown
   * at — so `α_approach ≈ α_stall / 1.3²`, since lift coefficient goes as the square of speed.
   *
   * It now really does come from the table. It used to be a hard-coded 17° sitting directly under
   * that sentence — right for this wing and silently wrong for the next one. M5 needed the same
   * number for the stall horn and the pre-stall buffet, so `soundConfigFor` derives it from the
   * largest horizontal panel's own aerofoil, and the HUD reads the same derivation.
   */
  const stallAlphaDeg = soundConfigFor(aircraft).stallAlphaRad / DEG2RAD;
  const hudLimits = { stallAlphaDeg, approachAlphaDeg: stallAlphaDeg / 1.69 };

  const menu = new MenuShell({
    settings: settingsStore,
    bindings,
    onFly: () => startFlying(),
    onResume: () => startFlying(),
    onRestart: () => {
      if (lastSpawn !== null) spawnAt(lastSpawn);
      startFlying();
    },
    currentAircraftId: () => aircraft.config.id,
    fleet: () => AIRCRAFT,
    /*
     * Changing aeroplane reloads the page with `?aircraft=<id>`.
     *
     * Stated plainly because it is a real limitation and not a design: the flight model, the
     * cockpit's instrument faces, the sound set, the weapons and the exterior model are all built
     * once during bootstrap from the config, and swapping them in place means tearing down and
     * rebuilding six subsystems while the loop is running. A reload is two seconds and cannot be
     * subtly wrong. Doing it properly is a rebuild of `bootstrap` into something re-runnable, and
     * that belongs to whichever milestone first needs to change aircraft *without* a reload —
     * multiplayer, most likely.
     */
    onChangeAircraft: (aircraftId: string) => {
      if (aircraftId === aircraft.config.id) {
        startFlying();
        return;
      }
      const url = new URL(globalThis.location.href);
      url.searchParams.set('aircraft', aircraftId);
      /*
       * …and skip the menu on the way back in.
       *
       * Changing aeroplane is a reload (see above), and without this the pilot who picked one from
       * the fleet gets splash → main menu → FLY before they are in it — four interactions to
       * answer a question they already answered. They chose an aircraft; put them in it.
       */
      url.searchParams.set('nomenu', '1');
      globalThis.location.assign(url.toString());
    },
    onRearm: weapons.hasWeapons
      ? () => {
          weapons.rearm();
          audio.click();
        }
      : null,
    onGunnery: (course) => {
      setGunnery(course);
      startFlying();
    },
    gunneryActive: () => gunnery.active,
    armed: () => weapons.hasWeapons,
    onQuitToMenu: () => {
      loop.pause();
      menu.setScreen('main');
    },
    onOpenSpawnPicker: () => {
      startFlying();
      spawnPicker.setVisible(true);
    },
    onSound: () => audio.click(),
  });

  /**
   * Start a gunnery course, or leave one.
   *
   * Entering a course arms the aircraft and points the weapons at the field; leaving safes it and
   * hands back `NO_TARGETS`, which is what makes free flight weapons-cold rather than
   * weapons-hot-but-with-nothing-to-shoot.
   */
  function setGunnery(course: CourseId | null): void {
    if (course === null) {
      gunnery.end(weapons);
      targets = NO_TARGETS;
      combatView.clearTargets();
      effects.clear();
      setPill('weapons safe — free flight', 3_000);
      return;
    }
    if (!weapons.hasWeapons) {
      setPill(`the ${selected.name} is unarmed — fly the Vandal for gunnery`, 5_000);
      return;
    }

    /*
     * Where the course goes.
     *
     * The balloon run has a home — a course is a place, and this one is a canyon because the
     * whole exercise is that the ground is in the way. The drone circuits are put wherever the
     * aeroplane already is, so they can be started from any airport without a ferry flight.
     */
    const placement =
      course === 'balloons'
        ? CANYON_COURSE
        : {
            latitudeDeg: scene.world.aircraftGeodetic.latitudeDeg,
            longitudeDeg: scene.world.aircraftGeodetic.longitudeDeg,
            groundElevationM: Number.isNaN(scene.world.groundElevationM)
              ? 0
              : scene.world.groundElevationM,
          };

    gunnery.begin(course, placement, weapons);
    targets = gunnery.targets;
    combatView.buildTargets(gunnery.targets.all);
    const record = gunnery.records.recordFor(course);
    const best =
      record.bestTimeS === null ? 'no record yet' : `best ${formatTime(record.bestTimeS)}`;
    setPill(
      course === 'balloons'
        ? `balloon canyon — six balloons, guns only · ${best}`
        : `drone range — two targets · best ${record.bestScore}`,
      6_000,
    );
  }

  /** Leave the menu and hand the aeroplane back. */
  function startFlying(): void {
    menu.setScreen('none');
    if (loop.scheduler.paused) loop.resume();
    canvas.focus();
  }

  /**
   * Push the settings into everything that reads them.
   *
   * One function rather than a listener per subsystem: a settings change is rare and touching
   * eight things is cheap, and this way "what does this setting do" has one answer you can read.
   */
  function applySettings(settings: Readonly<Settings>): void {
    scene.cameraRig.setFieldOfView(settings.fovDeg, settings.cockpitFovDeg);
    scene.environment.setExposureBias(settings.exposureBias);
    controls.coordination.setEnabled(settings.coordinationAssist);
    controls.setMouseYokeEnabled(settings.mouseYoke);
    fighterHud.setVisible(settings.fighterHud);
    /*
     * The control hint strip lists key bindings, so on a phone it is a row of instructions for
     * hardware that is not there — clutter across the bottom of exactly the screen with the least
     * room for any. Gated here rather than by writing the setting, so a profile synced from a
     * phone does not silently turn the strip off on a desktop.
     */
    helpFooter.setVisible(settings.helpFooter && !touchTier);
    audio.applySettings(settings);
    helpFooter.setActive('mute', settings.muted);
    if (quality.mode !== settings.qualityMode) {
      quality.setMode(settings.qualityMode);
      applyQuality('user');
    }
  }
  settingsStore.events.on('change', (event) => applySettings(event.settings));

  /**
   * Fill the audio system's three state structs from the simulation.
   *
   * All three are owned by the audio system and mutated in place — nothing here allocates, and
   * nothing here hands the audio system an engine object. That is the whole point of the split
   * (see `audio/state.ts`): the same three structs could just as easily be filled from a network
   * packet, and a second aeroplane's sound would be a second `AudioSystem`.
   */
  function readAudio(dt: number): void {
    const state = fdm.state;
    const sound = audio.aircraft;

    sound.rpm = state.rpm;
    sound.powerW = state.powerW;
    sound.throttle = state.throttle;
    sound.engineRunning = state.engineRunning;
    sound.starterEngaged = state.starterEngaged;

    sound.indicatedAirspeedMps = state.indicatedAirspeedMps;
    sound.trueAirspeedMps = state.trueAirspeedMps;
    sound.groundSpeedMps = state.groundSpeedMps;
    sound.alphaRad = state.alphaRad;
    sound.speedOfSoundMps = fdm.atmosphereSample.speedOfSoundMps;

    sound.onGround = state.onGround;
    sound.weightOnWheelsN = fdm.gear.weightOnWheelsN;
    sound.touchdownRateMps = fdm.gear.touchdownRateMps;
    /*
     * The gear servo's two inputs, which nothing wrote until v1.1.
     *
     * `ground.ts` gates the servo on `|target − position| > 0.01` and both fields sat at their
     * initial 1, so the difference was permanently zero and the servo could never run on any
     * aeroplane. It was not audible even in principle — which is why nobody noticed that the
     * gear it was meant to accompany was never moving either.
     *
     * Position is the FDM's animated fraction; target is the lever. The lock clunk falls out of
     * the two converging, so it needs no separate event.
     */
    sound.gearPosition = state.gearFraction;
    sound.gearTargetPosition = controls.gearDown ? 1 : 0;
    /*
     * What is under the wheels.
     *
     * The surface of the runway this session spawned on, which is right almost all of the time
     * and is knowingly approximate: taxiing off onto the grass still sounds like asphalt. Doing
     * it properly means testing the aircraft's position against each runway's polygon every
     * tick, which belongs with the terrain material's own surface classification rather than
     * being invented here for one sound.
     */
    sound.surface = lastSpawn?.runway.surface ?? 'asphalt';

    sound.flapAngleDeg = fdm.flapAngle;
    sound.flapTargetDeg = flapDetents[controls.flapDetentIndex] ?? 0;
    // A turbine's spool and its reheat are states the flight model owns; the audio system turns
    // them into a whine and a roar without knowing what an engine is. See `audio/audioSystem.ts`.
    sound.afterburnerFraction = fdm.state.afterburnerFraction;

    /*
     * The seeker's growl.
     *
     * A level rather than an event, because that is what it is: a continuous tone whose pitch and
     * roughness are the instrument the pilot flies the intercept by. `armed` gates it, so a cold
     * aeroplane is silent on the weapons bus by construction.
     */
    const weaponsSound = audio.weapons;
    weaponsSound.armed = weapons.armed;
    weaponsSound.seekerTone = weapons.seeker?.state.toneStrength ?? 0;
    weaponsSound.seekerLocked = weapons.seeker?.state.status === 'locked';

    // --- where the ears are ------------------------------------------------------------------
    const listener = audio.listener;
    listener.cockpitFraction = scene.cameraRig.cockpitFraction;
    listener.cloudiness = scene.clouds.cloudiness;
    // A paused simulation ducks the world rather than muting it: the menu's own clicks stay
    // audible, and coming back is a fade rather than a switch.
    listener.activity = loop.scheduler.paused ? 0 : 1;

    const camera = scene.camera;
    aeroToRenderVector(fdm.body.velocityNed, audioVelocity);
    listenerRight.setFromMatrixColumn(camera.matrixWorld, 0);
    if (!listenerSeeded) {
      listenerPrevious.copy(camera.position);
      listenerSeeded = true;
    }
    audio.observe(
      scene.aircraft.position,
      audioVelocity,
      camera.position,
      listenerPrevious,
      listenerRight,
      dt,
    );
    listenerPrevious.copy(camera.position);

    const weather = scene.environment.weather;
    audio.environment.surfaceWindMps = weather.windSpeedMps;
    audio.environment.precipitation = weather.precipitation;
  }

  /**
   * Fill the HUD's weapons state, or hand it `null`.
   *
   * `null` when the aircraft is not armed, and that single line is the whole of "free flight has
   * zero weapons UI". There is no branch in the HUD's drawing code, no visibility flag and nothing
   * to keep in sync — an unarmed aeroplane cannot produce a weapons state, so no weapons
   * symbology exists to be drawn.
   */
  function refreshWeaponsHud(): void {
    if (!weapons.armed || !weapons.hasWeapons) {
      fighterHud.setWeapons(null);
      return;
    }

    weaponsHud.selection = weapons.selection;
    weaponsHud.ammunition = weapons.cannon?.ammunition ?? 0;
    weaponsHud.missiles = weapons.missiles?.remaining ?? 0;
    weaponsHud.pipperAzimuthRad = weapons.sight.azimuthRad;
    weaponsHud.pipperElevationRad = weapons.sight.elevationRad;
    weaponsHud.gunRangeM = weapons.sight.rangeM;

    const seeker = weapons.seeker?.state;
    weaponsHud.seekerStatus = seeker?.status ?? 'none';
    weaponsHud.lockProgress = seeker?.lockProgress ?? 0;
    weaponsHud.targetRangeM = seeker?.rangeM ?? 0;
    weaponsHud.targetClosureMps = seeker?.closureMps ?? 0;
    weaponsHud.inRange = seeker?.inRange ?? false;
    weaponsHud.score = gunnery.points;
    weaponsHud.targetsRemaining = gunnery.remaining;
    weaponsHud.elapsedS = gunnery.score.elapsedS;

    /*
     * Where the target is, as an angle off the nose.
     *
     * Resolved against the same body axes the weapons use, so the box lands on the target rather
     * than near it. `NaN` when there is nothing tracked — see `fighterHud.ts` for why that
     * particular sentinel, and not zero.
     */
    weaponsHud.targetAzimuthRad = Number.NaN;
    weaponsHud.targetElevationRad = Number.NaN;
    if (seeker !== undefined && seeker.targetIndex >= 0 && seeker.targetIndex < targets.length) {
      const tracked = targets.at(seeker.targetIndex);
      toTargetNed.subVectors(tracked.positionNed, fdm.body.positionNed);
      const forward = toTargetNed.dot(bodyForward);
      if (forward > 1) {
        weaponsHud.targetAzimuthRad = Math.atan2(toTargetNed.dot(bodyRight), forward);
        weaponsHud.targetElevationRad = Math.atan2(toTargetNed.dot(bodyDown), forward);
      }
    }

    fighterHud.setWeapons(weaponsHud);
  }

  /**
   * Turn this tick's weapon events into sound.
   *
   * Rounds are counted by *differencing the magazine* rather than by the gun reporting each shot.
   * That is not a shortcut — it means the sound is driven by the same number the HUD shows and
   * the score counts, so the three cannot disagree about how many rounds were fired, which is
   * exactly the kind of divergence nobody notices until somebody counts.
   */
  function reportWeaponSounds(ammunitionBefore: number): void {
    const cannon = weapons.cannon;
    if (cannon !== null) {
      const fired = ammunitionBefore - cannon.ammunition;
      for (let i = 0; i < fired; i++) audio.fireCannon();
    }

    if (weapons.launch.fired) audio.launchMissile();

    const battery = weapons.missiles;
    if (battery !== null) {
      for (let i = 0; i < battery.detonationLength; i++) {
        const event = battery.detonations[i];
        audio.explosion(event.positionNed.distanceTo(fdm.body.positionNed));
      }
    }

    // A target dying is its own bang, and it is not the same event as the warhead going off —
    // a drone killed by gunfire never detonated anything.
    if (gunnery.killedThisTick > 0) {
      for (const target of gunnery.targets.all) {
        if (!target.justDied) continue;
        audio.explosion(target.positionNed.distanceTo(fdm.body.positionNed));
      }
    }
  }

  const environmentPanel = new EnvironmentPanel({
    environment: scene.environment,
    // Re-publish immediately rather than waiting for the next frame: the panel is usable while
    // the simulation is paused, and a slider that does nothing until you unpause is a slider
    // that appears broken.
    onChange: () => scene.refreshEnvironment(),
    onWeatherChange: () => applyWeatherToWind(),
  });

  const statusPill = document.createElement('div');
  statusPill.className = 'status-pill';
  statusPill.hidden = true;
  document.body.append(statusPill);

  /** A transient message; `holdMs` keeps it up even when nothing else wants the pill. */
  let pillHoldUntilMs = 0;
  let pillHoldText: string | null = null;

  function setPill(text: string | null, holdMs = 0): void {
    if (holdMs > 0 && text !== null) {
      pillHoldText = text;
      pillHoldUntilMs = performance.now() + holdMs;
    }
    if (text === null) {
      statusPill.hidden = true;
      return;
    }
    if (statusPill.textContent !== text) statusPill.textContent = text;
    statusPill.hidden = false;
  }

  /*
   * The first-flight director — see `ui/firstFlight.ts` for why it exists at all.
   *
   * Suppressed for the perf harness and the screenshot runner: both drive a real browser against
   * a fresh profile, so without this every published screenshot and the social card would carry
   * "hold = for full power".
   *
   * The key labels are read fresh on every phase change rather than captured here, so a pilot who
   * rebinds mid-flight is not told to press a key that no longer does anything.
   */
  const rotateKias =
    aircraft.config.reference?.rotateKias ??
    (aircraft.config.reference?.stallCleanKias ?? 60) * 1.15;
  const firstFlight = new FirstFlightDirector({
    storage,
    suppressed: () => perfHarness !== null || screenshotRun !== null,
    /*
     * Rotate speed comes from the aeroplane's own published reference, so the prompt says 55 kt in
     * the trainer and 122 in the business jet with no aircraft-specific branch here — CLAUDE.md's
     * non-negotiable #8. `reference` is optional on the config type; every shipped aircraft has a
     * `rotateKias`, and an aeroplane that did not would fall back to 1.15 × its clean stall, which
     * is the same margin the real number is chosen with.
     */
    rotateSpeedMps: knotsToMps(rotateKias),
    rotateKias,
    keys: () => {
      /*
       * The two axes label their key pairs *oppositely*, and this is the one place that has to
       * know it. `flightControls.updateFixed` drives each axis with (positive, negative) in call
       * order: throttle is called with `Equal` first, and `Equal` is the binding's `positive`;
       * pitch is called with `KeyS` first, and `KeyS` is the binding's `negative`. Measured rather
       * than assumed — holding the first argument drives the axis to +1, and a positive pitch
       * command deflects the elevator nose-up (`fdm.ts`'s `elevatorDeg`). `firstFlight.test.ts`
       * pins that the shipped bindings produce "=" and "S".
       */
      const pitch = bindings.axis('pitch').keys;
      const yaw = bindings.axis('yaw').keys;
      const throttle = bindings.axis('throttle').keys;
      return {
        throttleUp: keyLabel(throttle?.positive ?? null),
        rudderLeft: keyLabel(yaw?.negative ?? null),
        rudderRight: keyLabel(yaw?.positive ?? null),
        pitchUp: keyLabel(pitch?.negative ?? null),
        view: keyLabel(bindings.keyFor('cycleCamera')),
        airports: keyLabel(bindings.keyFor('airports')),
        menu: keyLabel(bindings.keyFor('menu')),
      };
    },
  });

  let pillNextUpdateMs = 0;
  function updateStatusPill(nowMs: number): void {
    if (nowMs < pillNextUpdateMs) return;
    pillNextUpdateMs = nowMs + 200;

    if (perfHarness !== null && !perfHarness.done) {
      setPill(`perf run — ${Math.round(perfHarness.progress(nowMs) * 100)}%`);
    } else if (fdm.damage.crashed) {
      setPill('crashed — R to reset');
    } else if (loop.scheduler.paused) {
      setPill('paused — P to resume');
    } else if (nowMs < pillHoldUntilMs && pillHoldText !== null) {
      setPill(pillHoldText);
    } else if (controls.isMouseYokeEnabled) {
      setPill('mouse yoke — Y to release · N to recentre');
    } else {
      /*
       * Below the hold, so the spawn pill still gets its four seconds to say where you are, and
       * above nothing else — once the aeroplane is flying this returns null forever and the pill
       * goes back to being empty.
       */
      setPill(
        firstFlight.advise(
          {
            onGround: fdm.state.onGround,
            throttle: controls.commands.throttle,
            indicatedAirspeedMps: fdm.state.indicatedAirspeedMps,
            heightAboveGroundM: fdm.state.heightAboveGroundM,
            groundSpeedMps: fdm.state.groundSpeedMps,
          },
          nowMs,
        ),
      );
    }
  }

  // --- quality plumbing -------------------------------------------------------------------------
  function applyQuality(reason: 'user' | 'governor'): void {
    const settings = quality.settings;
    applyQualityToRenderer(renderer, settings);
    scene.applyQuality(settings);
    post.applyQuality(settings);
    viewport = resizeRenderer(renderer, canvas, settings);
    scene.setViewport(viewport.widthPx, viewport.heightPx);
    loop.profiler.resetWindow();
    if (reason === 'user') {
      governor.notifyExternalChange(performance.now());
      quality.save();
    }
    diagnostics.tier = `${settings.tier} (${quality.mode})`;
    console.info(`${PRODUCT_NAME} quality: ${settings.tier} (${reason})`);
  }

  /** Select a camera, keep the cockpit's visibility with it, and remember the choice. */
  function selectCamera(mode: CameraMode): void {
    scene.setCameraMode(mode);
    settingsStore.patch({ cameraMode: mode });
  }

  /*
   * The shell sees keys first.
   *
   * A rebinding row has to swallow the key it is capturing rather than triggering whatever that
   * key currently does, and a menu's arrow keys must not also pitch the aeroplane. Capture phase,
   * so it beats `Keyboard`'s own listener; `stopPropagation` when it consumes one, so the flight
   * controls never see it.
   */
  globalThis.addEventListener(
    'keydown',
    (event: KeyboardEvent) => {
      if (menu.handleKeyDown(event)) event.stopPropagation();
    },
    { capture: true },
  );

  // --- key bindings ------------------------------------------------------------------------------
  keyboard.onPress('F3', () => perfOverlay.toggle());
  keyboard.onPress('KeyH', () => hud.toggle());
  keyboard.onPress('Slash', () => helpFooter.toggle());
  // `L` for location, not `M`: CLAUDE.md reserves `M` for mute, and M5 has now claimed it.
  keyboard.onPress('KeyL', () => spawnPicker.toggle());
  keyboard.onPress('Escape', () => {
    // Esc closes whatever overlay is up first, and only then reaches for the pause menu — so it
    // always means "back one step" rather than "sometimes back, sometimes pause".
    if (spawnPicker.visible || environmentPanel.visible) {
      spawnPicker.setVisible(false);
      environmentPanel.setVisible(false);
      return;
    }
    if (menu.isOpen) return; // the shell's own listener handles it
    loop.pause();
    menu.setScreen('pause');
  });
  keyboard.onPress('KeyP', () => loop.togglePause());
  keyboard.onPress('KeyM', () => {
    const muted = !settingsStore.settings.muted;
    settingsStore.patch({ muted });
    setPill(muted ? 'audio muted' : 'audio on', 2_000);
  });
  /*
   * `I` is the key in the ignition, and it means both things that key means.
   *
   * Held with the engine stopped it cranks (the hold is sampled on the physics tick, above);
   * pressed with the engine running it shuts down. Shutting down is refused in the air, and that
   * is a real limitation rather than a nanny rail: the propeller's power-coefficient table only
   * covers the range where the propeller absorbs power, so a dead engine at speed winds the shaft
   * to a stop instead of windmilling. See `sim/propulsion.ts`.
   */
  keyboard.onPress('KeyI', () => {
    if (!fdm.state.engineRunning) {
      controls.setIgnition(true);
      return;
    }
    if (!fdm.gear.onGround) {
      setPill('shut down on the ground only', 3_000);
      return;
    }
    controls.setIgnition(false);
    setPill('mags off — engine shutting down', 3_000);
  });
  keyboard.onPress('KeyR', () => {
    if (lastSpawn !== null) spawnAt(lastSpawn);
  });
  keyboard.onPress('KeyC', () => {
    scene.cycleCameraMode();
    settingsStore.patch({ cameraMode: scene.cameraMode });
  });
  keyboard.onPress('KeyO', () => {
    settingsStore.patch({ fighterHud: !settingsStore.settings.fighterHud });
  });
  /*
   * Tab cycles the weapon, per the CLAUDE.md bindings.
   *
   * Silently ignored on an unarmed aeroplane rather than reporting an error: the trainer has no
   * stores page, so there is nothing for the key to be wrong about.
   */
  keyboard.onPress('Tab', () => {
    if (!weapons.armed || !weapons.hasWeapons) return;
    const selection = weapons.cycleSelection();
    setPill(selection === 'guns' ? 'GUN' : 'MSL', 1_500);
    audio.click();
  });
  /*
   * `0` — start, switch, and leave the gunnery courses.
   *
   * A digit rather than a letter because every letter is already spoken for: `K` is the parking
   * brake (`input/flightControls.ts`), and binding this on top of it would have given one key two
   * jobs on an armed aeroplane. `0` sits beside the camera keys 1–5, which is where a mode switch
   * belongs anyway.
   */
  keyboard.onPress('Digit0', () => {
    /*
     * Also the escape hatch from a scripted run.
     *
     * A benchmark that has taken the controls should still hand them back when a human asks. It
     * ruins the measurement, which is correct — a human flying it had already ruined it.
     */
    if (canyonRun !== null) {
      canyonRun = null;
      setPill('scripted run released — you have the aeroplane', 3_000);
      return;
    }
    if (!gunnery.active) setGunnery('drones');
    else if (gunnery.course === 'drones') setGunnery('balloons');
    else setGunnery(null);
  });
  // `T`, per the CLAUDE.md default bindings — `E` is already the right rudder pedal.
  keyboard.onPress('KeyT', () => environmentPanel.toggle());
  keyboard.onPress('KeyJ', () => {
    const enabled = controls.toggleCoordination();
    setPill(enabled ? 'auto-coordination on' : 'auto-coordination off — your feet now', 3_000);
  });
  // Shift+Z centres the rudder trim; Z and X alone are the trim itself and are held, not tapped.
  keyboard.onPress('Backslash', () => {
    controls.centreRudderTrim();
    setPill('rudder and aileron trim centred', 2_000);
  });
  /*
   * `6` — the shutter, and it is a digit for the mnemonic.
   *
   * `1`–`5` choose the camera; `6` keeps what the chosen camera is looking at. Every letter on the
   * keyboard is already spoken for (the only free one, `G`, is reserved for the gear), and the
   * function keys are worse than they look: `F3` is already the perf overlay and on macOS the
   * whole row is Mission Control and media transport by default, so a screenshot key would also
   * skip the pilot's music.
   */
  keyboard.onPress('Digit6', () => {
    if (shareController.request()) shareButton.pressed();
  });
  keyboard.onPress('Digit1', () => selectCamera('cockpit'));
  keyboard.onPress('Digit2', () => selectCamera('chase'));
  keyboard.onPress('Digit3', () => selectCamera('flyby'));
  keyboard.onPress('Digit4', () => selectCamera('orbit'));
  keyboard.onPress('Digit5', () => selectCamera('free'));
  keyboard.onPress('BracketLeft', () => {
    if (quality.stepDown('user')) applyQuality('user');
  });
  keyboard.onPress('BracketRight', () => {
    if (quality.stepUp('user')) applyQuality('user');
  });
  keyboard.onPress('KeyU', () => {
    const order: Array<'auto' | QualityTier> = ['auto', ...TIERS];
    quality.setMode(order[(order.indexOf(quality.mode) + 1) % order.length]);
    applyQuality('user');
  });

  /**
   * How many distinct shader programs the renderer has built.
   *
   * Read out of three's pipeline cache, which is internal and therefore read defensively: a
   * version that moves it returns −1 and the harness simply reports no shader column rather than
   * throwing inside the frame loop.
   *
   * It is worth reaching for anyway. A pipeline build is the most expensive thing a frame can do
   * on WebGPU and it is invisible in every other statistic — it looks like render CPU with no draw
   * calls, no tiles and no materials to explain it. Differencing this per frame is the difference
   * between "a mysterious one-second frame" and "that frame compiled forty shaders".
   */
  function compiledProgramCount(): number {
    const pipelines = (renderer as unknown as {
      _pipelines?: { programs?: { vertex?: { size?: number }; fragment?: { size?: number } } };
    })._pipelines;
    const vertex = pipelines?.programs?.vertex?.size;
    const fragment = pipelines?.programs?.fragment?.size;
    if (typeof vertex !== 'number' || typeof fragment !== 'number') return -1;
    return vertex + fragment;
  }

  // --- optional scripted perf run -----------------------------------------------------------------
  const perfMarks: StreamingMarks = {
    tilesAdopted: 0,
    uploadedBytes: 0,
    materialsCreated: 0,
    pendingAdoptions: 0,
    resident: 0,
  };
  const perfSeconds = requestedPerfSeconds();
  const perfHarness =
    perfSeconds === null
      ? null
      : new PerfHarness({
          profiler: loop.profiler,
          quality,
          backend,
          durationS: perfSeconds,
          warmupS: requestedPerfWarmupSeconds(),
          ...(allocationProbe === null ? {} : { allocationProbe }),
          sceneName: 'KSFO circuit',
          terrainStats: () => scene.world.terrain.statistics(),
          // Reused rather than rebuilt: the harness reads this every frame, and an instrument
          // that allocates is an instrument that changes the thing it is measuring.
          streamingMarks: () => {
            const terrain = scene.world.terrain.statistics();
            perfMarks.tilesAdopted = terrain.tilesAdopted;
            perfMarks.uploadedBytes = terrain.uploadedBytes;
            perfMarks.materialsCreated = terrain.materialsCreated;
            perfMarks.pendingAdoptions = terrain.pendingAdoptions;
            perfMarks.resident = terrain.resident;
            return perfMarks;
          },
          // Which of the three passes inside `render()` a long frame was spent in. The chain's own
          // object, read only for a frame that is being kept — see `PostChain.phases`.
          renderPhases: () => post.phases,
          // The floating origin, so the frame it costs is attributable rather than merely slow.
          originRebases: () => scene.world.rebaseCount,
          onComplete: () => setPill('perf run complete', 10_000),
        });

  if (perfHarness !== null) {
    quality.setMode(quality.tier);
    perfOverlay.setVisible(true);
    // Three `performance.now()` calls a frame, and only for a run that has somebody to read them.
    post.measurePhases = true;
  }

  /*
   * `?shot=<name>` — a scripted screenshot. Pins the tier for the same reason the perf run does:
   * an image taken at whatever tier the governor happened to land on is not comparable to the last
   * one. See `game/screenshotRun.ts`.
   */
  const shotName = requestedShotName();
  const screenshotRun =
    shotName === null
      ? null
      : new ScreenshotRun({
          name: shotName,
          canvas,
          settleS: requestedSettleSeconds(),
          residentTiles: () => scene.world.terrain.statistics().resident,
          // `?card=1200x630` makes this the social card rather than a milestone screenshot.
          card: requestedCardSize(),
          // Cold load to flying. `bootMs` is assigned further down, well before any capture.
          bootMs: () => bootMs,
          /*
           * `?probe=clouds` — the cloud layer's own numbers, out through the image channel.
           *
           * This is the only way to interrogate the WebGL2 backend at all: it cannot finish
           * start-up in a browser that is not being composited, so an embedded devtools prompt is
           * not available and the throwaway Chrome `npm run shots` launches has no readable
           * console. `probeInternals` evaluates *both* screen-space depth conventions side by side
           * in one shader invocation, which is exactly the measurement that settled the identical
           * symptom on WebGPU — see the header of `clouds/volumetricClouds.ts`.
           */
          ...(requestedProbes() === 'clouds'
            ? {
                probes: async (): Promise<Record<string, unknown>> => ({
                  backend,
                  tier: quality.tier,
                  buffer: await scene.clouds.probe(renderer),
                  internals: await scene.clouds.probeInternals(renderer, scene.camera),
                }),
              }
            : {}),
          /*
           * `?probe=terrain` — which zoom levels are actually on screen at the shutter.
           *
           * The same channel and the same reason as the cloud probe above: three's WebGL backend
           * polls shader compilation from a `requestAnimationFrame` callback, so an embedded or
           * hidden browser never gets a frame — measured here as an aeroplane still sitting at
           * lat 0, lon 0 with the physics unticked after thirty seconds, in a build that works
           * perfectly in a real window. `npm run shots` launches the only visible Chrome around,
           * and nobody can read its console, so a number that would otherwise be typed into a
           * devtools prompt goes out beside the picture.
           *
           * What it is for: the quadtree now follows the *imagery* past the elevation source's
           * last zoom, and the only way to know that is working is to see z16 and z17 tiles in a
           * real selection. A histogram makes both halves visible at once — the deep tiles near
           * the aeroplane, and the shallow ones that still have to cover the horizon.
           */
          ...(requestedProbes() === 'terrain'
            ? {
                probes: async (): Promise<Record<string, unknown>> => {
                  const stats = scene.world.terrain.statistics();
                  const info = renderer.info.render;
                  return {
                    backend,
                    tier: quality.tier,
                    altitudeFt: Math.round(fdm.state.altitudeM / 0.3048),
                    aglFt: Math.round(fdm.state.heightAboveGroundM / 0.3048),
                    drawnZooms: scene.world.terrain.drawnZoomHistogram(),
                    resident: stats.resident,
                    selected: stats.selected,
                    failed: stats.failed,
                    substituted: stats.substituted,
                    cacheMB: Math.round((stats.cacheBytes / 1_048_576) * 10) / 10,
                    drawCalls: info.drawCalls,
                    triangles: info.triangles,
                  };
                },
              }
            : {}),
          onCaptured: (name) => setPill(`captured ${name}`, 10_000),
        });
  if (screenshotRun !== null) quality.setMode(quality.tier);

  // --- go -------------------------------------------------------------------------------------------
  // Compile every pipeline while the splash is still up. This is the difference between the
  // first second of flight being the worst frames of the session and being unremarkable: on
  // WebGPU each first-use pipeline build is milliseconds of main-thread work, and the terrain
  // alone has two variants that would otherwise land on whichever frames happen to adopt the
  // first textured and first untextured tile.
  /*
   * The hero model, fetched now — after the world is built, before the pipelines are compiled.
   *
   * This ordering is the whole reason it is here rather than beside the `FlightScene` constructor.
   * Later than the world, so several megabytes of aeroplane are not competing with the first ring
   * of terrain tiles for the connection while the player is looking at an empty sky. Earlier than
   * `precompile`, so the model's materials are in the scene when the pipelines are warmed and the
   * frame that first draws the aeroplane is not the frame that compiles it — which, at the ~10 ms
   * per `RenderObject` this renderer measures, is the difference between a hitch and nothing.
   *
   * It cannot fail the boot. `loadAircraftModel` resolves to `null` on any error and the built
   * airframe carries on.
   */
  setStatus('loading the aeroplane…');
  const exterior = await loadAircraftModel(aircraft.config.id);
  if (exterior !== null) {
    scene.setExteriorModel(exterior);
    /*
     * Move the plume onto the new exterior's nozzle.
     *
     * Not optional and not cosmetic: the built airframe is a 12 m jet with its exit at z 4.6 and
     * the baked one is a 14.7 m jet with its exit at z 6.6, so a plume left on the old anchor
     * would burn two metres inside the fuselage. `scene.nozzle` is read rather than
     * `exterior.nozzle` so this stays right if the model turns out to carry no anchor and the
     * scene keeps drawing the built airframe's tail.
     */
    if (afterburner !== null && scene.nozzle !== null) afterburner.setNozzle(scene.nozzle);
    console.info(
      `${PRODUCT_NAME} exterior: ${aircraft.config.id} — ${exterior.triangles} triangles, ` +
        `${exterior.measuredSpanM.toFixed(2)} m span (config ${aircraft.config.geometry.wingSpanM} m)`,
    );
  }

  /*
   * The cockpit interior, fetched alongside the exterior and for the same reasons.
   *
   * Sequential rather than concurrent with the exterior above, deliberately: the two together are
   * up to 8 MB, and the note above about not competing with the first ring of terrain tiles for
   * the connection applies twice as hard to two large assets at once. It is still before
   * `precompile`, which is the ordering that matters — the cockpit's materials are in the scene
   * when the pipelines are warmed, so the first frame in the cockpit view is not also the frame
   * that compiles it.
   *
   * Like the exterior, it cannot fail the boot: `loadCockpitModel` resolves to `null` on any error
   * and the procedural cockpit carries on, which is exactly the fallback it was built to be.
   */
  setStatus('loading the cockpit…');
  const interior = await loadCockpitModel(aircraft.config.id);
  if (interior !== null) {
    scene.cockpit.adoptModel(interior);
    console.info(
      `${PRODUCT_NAME} cockpit: ${aircraft.config.id} — ${interior.triangles} triangles, ` +
        `supplies ${interior.supplies}, panel at ` +
        `${interior.panel === null ? 'no measured face' : `${interior.panel.distanceM.toFixed(2)} m`}`,
    );
  }

  setStatus('compiling shaders…');
  /*
   * On the WebGL2 path this step **cannot finish while the page is not being composited**, and it
   * is worth saying so on the splash rather than leaving a mystery.
   *
   * Three's WebGL backend reports parallel shader-compile completion by polling
   * `COMPLETION_STATUS_KHR` from inside a `requestAnimationFrame` callback, and `compileAsync`
   * awaits all of those. A hidden, occluded or backgrounded window gets no animation frames at all
   * — measured here at **0 frames in 12 s with `visibilityState: 'hidden'`** — so the await simply
   * does not return, before `loop.start()` and therefore before anything else could rescue it.
   *
   * It self-heals the moment the window is looked at, which is why this is a message and not a
   * timeout: abandoning `compileAsync` half way would leave three holding cached pipelines whose
   * `_completeCompile` never ran, and the normal render path only completes a pipeline it creates
   * itself. An explained wait beats a broken pipeline.
   *
   * The WebGPU path takes about a second, so this timer almost never fires there.
   */
  const slowCompileNotice = globalThis.setTimeout(() => {
    setStatus('still compiling shaders — keep this window in the foreground');
  }, 6_000);
  await scene.precompile(renderer).catch((error: unknown) => {
    // Not fatal: without it the pipelines are built on first use, exactly as they used to be.
    console.warn(`${PRODUCT_NAME}: pipeline warm-up failed, falling back to first-use compilation —`, error);
  });
  globalThis.clearTimeout(slowCompileNotice);

  setStatus('ready');
  /*
   * Cold load to flying, measured rather than estimated.
   *
   * `performance.now()` is milliseconds since `timeOrigin`, which *is* navigation start — so this
   * one number is the whole acceptance criterion, covering the HTML, the bundle, the renderer's
   * `init()`, the world's construction and every pipeline the pre-compile built. It is taken here
   * because this is the instant the aeroplane becomes flyable: the splash is coming down and
   * `loop.start()` is a few lines below.
   *
   * Reported through the screenshot collector as well as the console, because the browser that can
   * be trusted to have a genuinely cold cache is the throwaway profile `npm run shots` launches,
   * and nobody is watching its console.
   */
  const bootMs = performance.now();
  console.info(`${PRODUCT_NAME} ready: ${(bootMs / 1000).toFixed(2)} s from navigation start (${backend})`);
  bootSplash?.classList.add('boot--hidden');
  globalThis.setTimeout(() => bootSplash?.remove(), 500);

  // Everything the player configured, pushed out now that every consumer exists.
  scene.setCameraMode(requestedCockpitView() ? 'cockpit' : settingsStore.settings.cameraMode);
  applySettings(settingsStore.settings);
  // After `applySettings`, which does not touch the chase distance but does re-apply the camera
  // mode — so a shot that asked for a framing keeps it.
  const chaseDistanceM = requestedChaseDistanceM();
  if (chaseDistanceM !== null) scene.cameraRig.setChaseDistance(chaseDistanceM);
  resizeHud();

  /*
   * Re-pin the tier for a scripted run, *after* the settings have been applied.
   *
   * The harness pins it at construction and `applySettings` put it straight back to whatever the
   * settings say — which is `auto` by default, so the governor was free to step down mid-run and a
   * measurement asked for at Medium was reported at Low. Applying the settings first and pinning
   * last is the order that makes the pin mean something.
   */
  if (perfHarness !== null || screenshotRun !== null) {
    quality.setMode(pinnedTier ?? quality.tier);
    applyQuality('user');
  }

  /*
   * A scripted run — a perf measurement or a screenshot — goes straight to the aeroplane.
   *
   * Not a convenience: a menu in front of the scene would make the harness measure a paused
   * simulator behind a dimmed overlay, and the screenshot would be of the menu.
   */
  /*
   * The scripted canyon run: move the world to the canyon, string the balloons, and arm.
   *
   * Done here rather than when the airport database lands, because this run has nothing to do
   * with an airport — it is anchored to a latitude and longitude in the middle of the Grand
   * Canyon, and waiting for 1.2 MB of airport data before starting would put the download inside
   * the measurement window.
   */
  if (canyonRun !== null) {
    scene.world.teleport(CANYON_COURSE.latitudeDeg, CANYON_COURSE.longitudeDeg);
    scene.setObserver(CANYON_COURSE.latitudeDeg, CANYON_COURSE.longitudeDeg);
    setGunnery('balloons');
    scene.setCameraMode('chase');
  }

  if (perfHarness !== null || screenshotRun !== null || requestedSkipMenu()) {
    menu.setScreen('none');
  } else {
    loop.pause();
    menu.setScreen('main');
  }

  // Arm the governor's start-up grace period *before* the first frame. Shader compilation and
  // first-use pipeline creation make the opening seconds the worst frames of the session; left
  // to steer on them the governor walks a perfectly capable machine down to Low before the
  // scene has even settled, and (until the credit rule) never climbs back.
  governor.start(performance.now());
  loop.start();

  // A debug handle. Deliberately kept in the shipped build: a flight simulator is a system you
  // diagnose by poking at it while it runs, and "open the console and look at the tile cache"
  // is worth far more than the couple of hundred bytes it costs.
  (globalThis as { __SKYSIM__?: unknown }).__SKYSIM__ = {
    loop,
    scene,
    world: scene.world,
    fdm,
    quality,
    governor,
    controls,
    keyboard,
    post,
    renderer,
    /**
     * The audio system, and its debug triggers for the voices nothing drives yet.
     *
     * `__SKYSIM__.audio.debug.cannon()`, `.missile()`, `.explosion(300)`, `.turbine(0.9, 1)` and
     * `.touchdown(3, 30)`. These are M6's voices: the synths are finished and reachable, and what
     * they are waiting for is a fighter to be attached to.
     */
    audio,
    /**
     * The armament and the range.
     *
     * `__SKYSIM__.gunnery('drones')` sets a course up and arms the aeroplane;
     * `__SKYSIM__.weapons.cannon.ammunition` and `.seeker.state` say what it is doing. Kept in the
     * shipped build for the same reason as everything else on this handle: a weapon that misses is
     * diagnosed by reading the state it missed with, and "open the console" beats a rebuild.
     */
    weapons,
    effects,
    /**
     * The reheat plume, or null on an aeroplane without one.
     *
     * Here because the one thing worth checking about it cannot be seen unless the afterburner is
     * lit, and lighting it means flying: the plume must sit on **the showing exterior's** nozzle,
     * and the two exteriors of the Vandal put theirs 2.3 m apart. So the check is arithmetic
     * rather than photographic —
     *
     * ```js
     * const s = __SKYSIM__.scene; let n = null;
     * s.aircraft.traverse((o) => { if (o.name === 'NOZZLE') n = o; });
     * n.getWorldPosition(a); __SKYSIM__.afterburner.group.getWorldPosition(b);  // a ≈ b
     * ```
     *
     * — and it is the closing check on M7.6's inverted nozzle dependency.
     */
    afterburner,
    gunnery,
    /** Start or leave a course: `'drones'`, `'balloons'`, or `null`. */
    gunneryMode: (course: CourseId | null) => setGunnery(course),
    /** `await __SKYSIM__.probeClouds()` — what the cloud pass actually produced this frame. */
    probeClouds: () => scene.clouds.probe(renderer),
    /**
     * `await __SKYSIM__.probeCloudInternals()` — the march's *intermediates*: interval length,
     * density, and the raw volume fetch. What `probeClouds` cannot tell you is whether an empty
     * buffer means the geometry rejected every ray or the density did; this can.
     */
    probeCloudInternals: () => scene.clouds.probeInternals(renderer, scene.camera),
  };

  /*
   * The service worker, also off the critical path.
   *
   * After `loop.start()` on purpose: registration is a network round trip and an install that
   * downloads the whole app shell, and none of that should sit between the splash and the first
   * frame. The only thing it changes about *this* session is that a later one starts instantly.
   *
   * Off in development (it would cache unbundled modules and fight HMR) and off for every
   * scripted run (a warm cache would make a perf run measure a load nobody will ever perform).
   * See `core/serviceWorker.ts`.
   */
  const updates = new AppUpdates();
  updates.events.on('updateReady', (event) => {
    notices.show({
      id: 'update',
      text: `A newer build of ${PRODUCT_NAME} is ready.`,
      priority: 10,
      dismissible: true,
      action: { label: 'RELOAD', run: () => event.apply() },
    });
  });
  void updates.register({
    enabled: import.meta.env.PROD && perfHarness === null && screenshotRun === null,
  });

  // --- the airport database, off the critical path ---------------------------------------------------
  // Deliberately after `loop.start()`: it is 1.2 MB, and the terrain is already streaming and
  // the world already flyable while it downloads. When it lands the aircraft is placed properly.
  void AirportDatabase.load(new URL(AIRPORT_DB_URL, document.baseURI).href)
    .then((database) => {
      scene.world.setDatabase(database);
      spawnPicker.setDatabase(database);

      /*
       * Where to open.
       *
       * A `?icao=` argument wins, because it is what a bug report or a perf run is pinning down.
       * Otherwise the last flight, which is what a returning pilot expects. Otherwise the default
       * field. The runway is matched by *identifier* rather than by index — an airport database
       * update can reorder them, and spawning on the wrong strip is worse than spawning on the
       * longest one.
       */
      const remembered = loadLastFlight(storage);
      const requested = requestedIcao() ?? remembered?.icao ?? DEFAULT_ICAO;
      const airport = database.byIcao(requested) ?? database.byIcao(DEFAULT_ICAO);
      const runway: Runway | null = airport === null ? null : longestRunway(airport);
      if (airport !== null && runway !== null) {
        spawnPicker.setQuery(airport.icao);
        const matched =
          remembered !== null && airport.icao === remembered.icao
            ? (airport.runways.find(
                (candidate) =>
                  candidate.lowIdent === remembered.runwayIdent ||
                  candidate.highIdent === remembered.runwayIdent,
              ) ?? runway)
            : runway;
        const end =
          remembered !== null && matched.highIdent === remembered.runwayIdent ? 'high' : 'low';
        spawnAt({
          icao: airport.icao,
          runway: matched,
          end: requestedIcao() !== null ? 'low' : end,
          mode: requestedSpawnMode(),
        });
      }
      scene.world.showNearbyAirports();
      console.info(
        `${PRODUCT_NAME} airports: ${database.airportCount.toLocaleString()} loaded · ` +
          scene.world.attributions.join(' · '),
      );
    })
    .catch((error: unknown) => {
      // Not fatal: the terrain streams regardless, and the sim is still flyable over it.
      console.warn(`${PRODUCT_NAME}: the airport database could not be loaded —`, error);
      setPill('airport data unavailable — flying over open terrain', 6_000);
    });

  let pausedByVisibility = false;
  /** Set while a perf run is waiting to see whether a hidden window ever comes back. */
  let hiddenPerfTimer: ReturnType<typeof globalThis.setTimeout> | undefined;

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      // A hidden tab gets no animation frames, so a perf run in one does not slow down — it
      // stops. Let the harness decide whether that is fatal: mid-measurement it is, during the
      // warm-up it just means warming up again.
      if (perfHarness !== null && !perfHarness.done) {
        perfHarness.markHidden();
        // …and if the window never comes back, nothing will ever run again to notice. A timer
        // is the only thing still alive in a hidden tab, so it is what has to say so: without
        // this the runner waits out its whole timeout and reports nothing at all, which is the
        // single most annoying way for a tool to fail.
        globalThis.clearTimeout(hiddenPerfTimer);
        hiddenPerfTimer = globalThis.setTimeout(() => {
          if (document.hidden && perfHarness !== null && !perfHarness.done) {
            perfHarness.abort(
              'the window was hidden for 20 s — a background or occluded window gets no ' +
                'animation frames. Keep it in the foreground, or use `npm run perf -- --dedicated`',
            );
          }
        }, 20_000);
      }
      if (!loop.scheduler.paused) {
        pausedByVisibility = true;
        loop.pause();
      }
      return;
    }
    // The first frame back after a stalled requestAnimationFrame is enormous; left in the
    // window it drags the 1 %-low down and the governor drops a tier for no reason. Coming
    // back from a background tab is the same problem as booting — the statistics need a few
    // seconds to become about this machine again — so it gets the same grace period rather
    // than the two-second settle, which is not long enough to refill the window.
    globalThis.clearTimeout(hiddenPerfTimer);
    loop.profiler.resetWindow();
    governor.start(performance.now());
    if (pausedByVisibility) {
      pausedByVisibility = false;
      loop.resume();
    }
  });

  globalThis.addEventListener('beforeunload', () => {
    loop.stop();
    resizeObserver.disconnect();
    keyboard.detach();
    pointer.detach();
    helpFooter.dispose();
    shareButton.dispose();
    notices.dispose();
    touchControls?.dispose();
    landscapePrompt?.dispose();
    spawnPicker.dispose();
    environmentPanel.dispose();
    post.dispose();
    scene.dispose();
    audio.dispose();
    renderer.dispose();
  });
}

// --- query-string entry points -------------------------------------------------------------------

/**
 * `?allocprobe=1` — break the frame's heap allocation down by loop channel.
 *
 * For answering "which part of the frame is making the garbage", which the whole-heap figure in the
 * perf report cannot. See `AllocationProbe` for why every number it gives is a lower bound.
 */
function requestedAllocationProbe(search: string = globalThis.location?.search ?? ''): boolean {
  const value = new URLSearchParams(search).get('allocprobe');
  return value !== null && value !== '0';
}

/** `?icao=LOWI` — start somewhere specific. Used by the perf harness and by bug reports. */
function requestedIcao(search: string = globalThis.location?.search ?? ''): string | null {
  const value = new URLSearchParams(search).get('icao');
  return value === null || value.trim() === '' ? null : value.trim().toUpperCase();
}

/** `?spawn=final` — start on approach rather than on the threshold. */
function requestedSpawnMode(search: string = globalThis.location?.search ?? ''): 'threshold' | 'final' {
  return new URLSearchParams(search).get('spawn') === 'final' ? 'final' : 'threshold';
}

function requestedLatitude(search: string = globalThis.location?.search ?? ''): number | null {
  const value = Number.parseFloat(new URLSearchParams(search).get('lat') ?? '');
  return Number.isFinite(value) ? value : null;
}

function requestedLongitude(search: string = globalThis.location?.search ?? ''): number | null {
  const value = Number.parseFloat(new URLSearchParams(search).get('lon') ?? '');
  return Number.isFinite(value) ? value : null;
}

/**
 * `?time=dusk` or `?time=17.5` — a named preset or a local solar hour.
 *
 * Two forms because they answer different questions. A preset is what you want for a screenshot
 * or a bug report ("golden hour, wherever this is, whatever the date"); an hour is what you want
 * when reproducing something that happened at a specific time.
 */
function requestedTimePreset(search: string = globalThis.location?.search ?? ''): TimePresetName | null {
  const value = new URLSearchParams(search).get('time')?.trim().toLowerCase();
  return TIME_PRESETS.find((preset) => preset === value) ?? null;
}

/**
 * `?time=now` — fly under the sun that is actually up, wherever the spawn airport is.
 *
 * An option rather than the default. "Real now" sounds like the obvious thing for a simulator to
 * do and it is a bad default: half of all sessions would open in the dark, and a screenshot or a
 * perf run would be lit differently every time it was taken. The default is a fixed morning (see
 * `DEFAULT_LOCAL_SOLAR_HOURS`); this is here for when the point *is* the real sky.
 */
function requestedRealNow(search: string = globalThis.location?.search ?? ''): boolean {
  return new URLSearchParams(search).get('time')?.trim().toLowerCase() === 'now';
}

/**
 * `?rebase=<metres>` — move the floating-origin threshold, to diagnose periodic jitter.
 *
 * A rebase costs one long frame: 19.5 ms against an 8.4 ms median on the reference machine. Its
 * *rate* is proportional to airspeed — every 3.2 s at 300 kt, every 1.9 s at 480 — which makes it
 * the one mechanism that produces a periodic disturbance getting worse with speed and identical in
 * every camera view, since frame pacing does not care where the camera is.
 *
 * A pilot reported exactly that and it survived four geometric fixes and twenty-eight refuted
 * hypotheses. This turns the question into one page load: fly the same profile at `?rebase=500`
 * (the default) and `?rebase=8000`, and see whether the disturbance thins out with the rebases.
 *
 * **A diagnostic, not a setting.** Raising it makes each rebase dramatically more expensive — the
 * cost scales superlinearly with how far the world moves — which is why 500 m is the shipped value.
 */
function requestedRebaseDistanceM(search: string = globalThis.location?.search ?? ''): number | null {
  const raw = new URLSearchParams(search).get('rebase');
  if (raw === null) return null;
  const value = Number.parseFloat(raw);
  return Number.isFinite(value) ? value : null;
}

function requestedTimeHours(search: string = globalThis.location?.search ?? ''): number | null {
  const raw = new URLSearchParams(search).get('time');
  if (raw === null) return null;
  const value = Number.parseFloat(raw);
  return Number.isFinite(value) ? value : null;
}

/** `?date=2024-12-21` — pins the date, so a solstice can be reproduced exactly. */
function requestedUtc(search: string = globalThis.location?.search ?? ''): Date | null {
  const raw = new URLSearchParams(search).get('date');
  if (raw === null) return null;
  const parsed = new Date(`${raw}T12:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * `?view=cockpit` — start in the cockpit rather than behind the aeroplane.
 *
 * Exists for the scripted screenshots: an approach-lighting shot has to be taken from where the
 * pilot is looking, and the chase camera is behind and above, which puts the runway and its PAPI
 * somewhere below the nose instead of in the middle of the frame. `C` still cycles it at runtime.
 */
/**
 * Chase-camera distance [m], for the scripted aircraft portraits. Null leaves the rig's default.
 *
 * `?chase=14`. Out of range or unparseable is ignored rather than clamped-and-applied, so a typo
 * in a shot's query string produces the normal view instead of a silently different one.
 */
function requestedChaseDistanceM(search: string = globalThis.location?.search ?? ''): number | null {
  const value = Number.parseFloat(new URLSearchParams(search).get('chase') ?? '');
  return Number.isFinite(value) && value >= 5 && value <= 400 ? value : null;
}

function requestedCockpitView(search: string = globalThis.location?.search ?? ''): boolean {
  return new URLSearchParams(search).get('view')?.trim().toLowerCase() === 'cockpit';
}

/** `?final=<nm>` — how far out a `?spawn=final` starts. Three miles unless asked otherwise. */
function requestedFinalDistanceNm(search: string = globalThis.location?.search ?? ''): number {
  const raw = new URLSearchParams(search).get('final');
  if (raw === null) return 3;
  const value = Number.parseFloat(raw);
  return Number.isFinite(value) && value > 0 && value < 30 ? value : 3;
}

/** `?debug=clouds` — show an intermediate render buffer instead of the finished frame. */
function requestedDebugView(
  search: string = globalThis.location?.search ?? '',
): 'none' | 'clouds' | 'cloudAlpha' {
  const value = new URLSearchParams(search).get('debug')?.trim().toLowerCase();
  if (value === 'clouds') return 'clouds';
  if (value === 'cloudalpha') return 'cloudAlpha';
  return 'none';
}

/**
 * `?tier=high` — pin a quality tier instead of letting the governor choose.
 *
 * For measurement, not for play: the settings UI is the way a player picks a tier. See the note
 * at the call site for why a perf run cannot otherwise test a tier-gated feature at all.
 */
function requestedTier(search: string = globalThis.location?.search ?? ''): QualityTier | null {
  const value = new URLSearchParams(search).get('tier')?.trim().toLowerCase();
  return TIERS.find((tier) => tier === value) ?? null;
}

/**
 * `?nomenu=1` — open straight into the aeroplane.
 *
 * For a bug report or a demo link, where the point is the thing behind the menu. The perf harness
 * and the screenshot runner do this on their own, so this is only for a human sharing a URL.
 */
function requestedSkipMenu(search: string = globalThis.location?.search ?? ''): boolean {
  const value = new URLSearchParams(search).get('nomenu');
  return value !== null && value !== '0';
}

/** `?weather=broken` — one of the METAR sky-condition presets. */
function requestedWeather(search: string = globalThis.location?.search ?? ''): WeatherPresetName | null {
  const value = new URLSearchParams(search).get('weather')?.trim().toLowerCase();
  return WEATHER_PRESETS.find((preset) => preset === value) ?? null;
}
